import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, Box, Button, TextField, MenuItem, Grid, Accordion, AccordionSummary,
    AccordionDetails, Dialog, Stack, IconButton, Switch, FormControlLabel, Checkbox,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { actualizarEstadoConceptoContabilidad, crearConceptoPredefinidoContabilidad, deleteGasto, deleteIngreso, editarConceptoContabilidad, editarConceptoPredefinidoContabilidad, eliminarConceptoContabilidad, eliminarConceptoPredefinidoContabilidad, getDatosContables, gestionarAnyoContabilidad, getTicketsContabilidad, marcarGastoPagado, marcarIngresoPagado, marcarTicketPagado } from '../../services/apis';
import { AnadirIngresoDialog } from './AnadirIngresoDialog';
import { EditarIngresoDialog } from './EditarIngresoDialog';
import { AnadirTipoIngresoDialog } from './AnadirTipoIngresoDialog';
import { AnadirGastoDialog } from './AnadirGasto.Dialog';
import { EditarGastoDialog } from './EditarGastoDialog';
import { RegistroAccionesContabilidadDialog } from './RegistroAccionesContabilidadDialog';
import { ImagenPrivada } from '../../utils/ImagenPrivada';
import { borrarTokenSesion } from '../../utils/cookies';

const formatoEuro = (valor) => {
    const numero = parseFloat(valor);
    return (isNaN(numero) ? 0 : numero).toLocaleString('ca-ES', {
        style: 'currency',
        currency: 'EUR'
    });
};

const formatoGasto = (valor) => formatoEuro(-Math.abs(Number(valor) || 0));
const claseImporte = (valor) => Number(valor) > 0 ? 'accounting-amount-positive' : Number(valor) < 0 ? 'accounting-amount-negative' : '';

const ResumenImportes = ({ confirmado, pendiente = 0, total, esGasto = false, incluirPendiente = true, pendientePrimero = false, etiquetaConfirmado = 'Confirmat' }) => {
    const formato = esGasto ? formatoGasto : formatoEuro;
    const clase = (valor) => esGasto ? 'accounting-amount-negative' : claseImporte(valor);
    const importePendiente = incluirPendiente && <Box className="accounting-amount-summary-item">
        <span>Pendent</span>
        <strong className={clase(pendiente)}>{formato(pendiente)}</strong>
    </Box>;
    const importeConfirmado = <Box className="accounting-amount-summary-item">
        <span>{etiquetaConfirmado}</span>
        <strong className={clase(confirmado)}>{formato(confirmado)}</strong>
    </Box>;

    return (
        <Box className={`accounting-amount-summary ${!incluirPendiente ? 'accounting-amount-summary-compact' : ''}`} sx={{ minHeight: { xs: 0, md: '8.5vh' } }}>
            <Box className={`accounting-amount-summary-values ${!incluirPendiente ? 'accounting-amount-summary-values-single' : ''}`}>
                {pendientePrimero && importePendiente}
                {importeConfirmado}
                {!pendientePrimero && importePendiente}
            </Box>
            <Box className="accounting-amount-summary-total">
                <span>Total</span>
                <strong className={clase(total)}>{formato(total)}</strong>
            </Box>
        </Box>
    );
};

const CabeceraListaContable = ({ titulo, cantidad, confirmado, pendiente, total, esGasto = false, compensado = 0, etiqueta }) => (
    <Box className="accounting-list-header">
        <Typography variant="h6" className="accounting-list-title">{titulo} ({cantidad})</Typography>
        <Box className="accounting-list-header-amounts">
            <ResumenImportes confirmado={confirmado} pendiente={pendiente} total={total} esGasto={esGasto} pendientePrimero etiquetaConfirmado={etiqueta} />
            {/* {Number(compensado || 0) > 0 && <Typography className="accounting-compensated-text accounting-list-compensated">Compensat: {formatoEuro(compensado)}</Typography>} */}
        </Box>
    </Box>
);

const ResumenGeneralApartado = ({
    ingresosConfirmados,
    ingresosPendientes,
    ingresosTotal,
    gastosConfirmados,
    gastosPendientes,
    gastosTotal,
    balanceConfirmado,
    balanceTotal,
    className = ''
}) => (
    <Stack direction="row" className={`accounting-summary-totals ${className}`} sx={{ gap: 1 }}>
        <Box className="accounting-status-group" sx={{ bgcolor: 'var(--verde)', borderRadius: 1 }}>
            <Typography component="span" className="accounting-status-label">Ingressos</Typography>
            <ResumenImportes confirmado={ingresosConfirmados} pendiente={ingresosPendientes} total={ingresosTotal} />
        </Box>
        <Box className="accounting-status-group" sx={{ bgcolor: 'var(--rojo)', borderRadius: 1 }}>
            <Typography component="span" className="accounting-status-label">Despeses</Typography>
            <ResumenImportes confirmado={gastosConfirmados} pendiente={gastosPendientes} total={gastosTotal} esGasto />
        </Box>
        <Box className="accounting-status-group accounting-balance-status" sx={{ bgcolor: 'var(--azul)', borderRadius: 1 }}>
            <Typography component="span" className="accounting-status-label">{'Balan\u00e7'}</Typography>
            <ResumenImportes confirmado={balanceConfirmado} total={balanceTotal} incluirPendiente={false} />
        </Box>
    </Stack>
);

const formatoFecha = (valor) => {
    if (!valor) {
        return '';
    }

    const texto = String(valor);
    // Las fechas de los tiquets son la fecha del evento, sin hora. Si se
    // convierten con Date, JavaScript las trata como UTC y desplaza la hora.
    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
        const [anyo, mes, dia] = texto.split('-');
        return `${dia}/${mes}/${String(anyo).slice(-2)}`;
    }

    const fecha = new Date(texto.replace(' ', 'T'));
    if (Number.isNaN(fecha.getTime())) {
        return '';
    }

    const dia = fecha.getDate();
    const mes = fecha.getMonth() + 1;
    const anyo = String(fecha.getFullYear()).slice(-2);
    const hora = String(fecha.getHours()).padStart(2, '0');
    const minutos = String(fecha.getMinutes()).padStart(2, '0');

    return `${dia}/${mes}/${anyo} - ${hora}:${minutos}`;
};

export const Contabilidad = () => {
    const [datosContables, setDatosContables] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [contadorError, setContadorError] = useState(0);
    const [modalAddOpen, setModalAddOpen] = useState(false);
    const [modalEditOpen, setModalEditOpen] = useState(false);
    const [modalTipoIngresoOpen, setModalTipoIngresoOpen] = useState(false);
    const [ingresoSeleccionado, setIngresoSeleccionado] = useState(null);
    const [tiposIngreso, setTiposIngreso] = useState([]);
    const [currenApartado, setCurrenApartado] = useState(null);
    const [modalAnadirGastoOpen, setModalAnadirGastoOpen] = useState(false);
    const [modalEditarGastoOpen, setModalEditarGastoOpen] = useState(false);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
    const currentYear = new Date().getFullYear();
    const [anioSeleccionado, setAnioSeleccionado] = useState(currentYear);
    const [gastoImagen, setGastoImagen] = useState(null);
    const [listaAnios, setListaAnios] = useState([currentYear]);
    const [ticketsContables, setTicketsContables] = useState([]);
    const [filtroTicketMiembro, setFiltroTicketMiembro] = useState('');
    const [filtroTicketEvento, setFiltroTicketEvento] = useState('');
    const [filtroTicketEstado, setFiltroTicketEstado] = useState('pendents');
    const [filtroTicketConcepto, setFiltroTicketConcepto] = useState('');
    const [filtroTicketDesde, setFiltroTicketDesde] = useState('');
    const [filtroTicketHasta, setFiltroTicketHasta] = useState('');
    const [vistaTickets, setVistaTickets] = useState('usuario');
    const [errorTicket, setErrorTicket] = useState('');
    const [ticketPagoParcial, setTicketPagoParcial] = useState(null);
    const [importePagoParcial, setImportePagoParcial] = useState('');
    const [pagoTicketCompensado, setPagoTicketCompensado] = useState(false);
    const [ticketDescompensar, setTicketDescompensar] = useState(null);
    const [descompensandoTicket, setDescompensandoTicket] = useState(false);
    const [nuevoAnyo, setNuevoAnyo] = useState(currentYear + 1);
    const [dialogAnyo, setDialogAnyo] = useState(false);
    const [conceptoEditar, setConceptoEditar] = useState(null);
    const [nombreConcepto, setNombreConcepto] = useState('');
    const [conceptoActivo, setConceptoActivo] = useState(true);
    const [conceptoEliminar, setConceptoEliminar] = useState(null);
    const [errorConcepto, setErrorConcepto] = useState('');
    const [opciones, setOpciones] = useState(0);
    const [rolActual, setRolActual] = useState('');
    const puedeRegistrarIngresos = ['901', '986'].includes(String(rolActual));
    const puedeGestionarConceptosPredefinidos = ['901', '986'].includes(String(rolActual));
    const [usuarioActualId, setUsuarioActualId] = useState('');
    const [dialogRegistros, setDialogRegistros] = useState(false);
    const [idApartado, setApartado] = useState(null);
    const [gastoEliminar, setGastoEliminar] = useState(null);
    const [eliminandoGasto, setEliminandoGasto] = useState(false);
    const [errorEliminarGasto, setErrorEliminarGasto] = useState('');
    const [ingresoEliminar, setIngresoEliminar] = useState(null);
    const [eliminandoIngreso, setEliminandoIngreso] = useState(false);
    const [errorEliminarIngreso, setErrorEliminarIngreso] = useState('');
    const [gastoPagado, setGastoPagado] = useState(null);
    const [marcandoPagado, setMarcandoPagado] = useState(false);
    const [errorMarcarPagado, setErrorMarcarPagado] = useState('');
    const [ingresoPagado, setIngresoPagado] = useState(null);
    const [importeIngresoPagado, setImporteIngresoPagado] = useState('');
    const [marcandoIngresoPagado, setMarcandoIngresoPagado] = useState(false);
    const [errorIngresoPagado, setErrorIngresoPagado] = useState('');
    const [ingresoDesmarcarPagado, setIngresoDesmarcarPagado] = useState(null);
    const [desmarcandoIngresoPagado, setDesmarcandoIngresoPagado] = useState(false);
    const [errorDesmarcarIngresoPagado, setErrorDesmarcarIngresoPagado] = useState('');
    const [pagoUsuarioApartado, setPagoUsuarioApartado] = useState(null);
    const [pagoGeneralApartado, setPagoGeneralApartado] = useState(null);
    const [pagosDesplegados, setPagosDesplegados] = useState({});
    const [conceptoUsuariosDesplegados, setConceptoUsuariosDesplegados] = useState({});
    const [conceptoPredefinidoDialog, setConceptoPredefinidoDialog] = useState(null);
    const [nombreConceptoPredefinido, setNombreConceptoPredefinido] = useState('');
    const [apartadoConceptoPredefinidoId, setApartadoConceptoPredefinidoId] = useState('');
    const [guardandoConceptoPredefinido, setGuardandoConceptoPredefinido] = useState(false);
    const [errorConceptoPredefinido, setErrorConceptoPredefinido] = useState('');
    const [conceptoPredefinidoEliminar, setConceptoPredefinidoEliminar] = useState(null);

    const cargarDatos = async () => {
        try {
            const response = await getDatosContables(anioSeleccionado);
            const payload = response?.data || {};
            setDatosContables(payload.data || []);
            setUsuarios(payload.usuarios || []);
            setOpciones(payload.opciones);
            setRolActual(payload.rol_actual ?? '');
            setUsuarioActualId(payload.usuario_actual || '');
            const anios = payload.anios || [];
            if (anios.length) setListaAnios(anios.map((item) => Number(item.anyo)));
            const tickets = await getTicketsContabilidad(anioSeleccionado);
            setTicketsContables(tickets?.data || []);
        } catch (error) {
            setContadorError(prev => prev + 1);
            if (contadorError > 3) {
                localStorage.clear();
                window.location.href = "/login";
                borrarTokenSesion();
            }
        }
    };

    useEffect(() => {
        cargarDatos();
    }, [anioSeleccionado]);

    useEffect(() => {
        const nuevosTipos = new Set();
        datosContables.forEach(apartado =>
            (apartado.tipos_ingresos ?? []).forEach(tipo =>
                nuevosTipos.add(tipo.tipo_ingreso)
            )
        );
        setTiposIngreso(Array.from(nuevosTipos));
    }, [datosContables]);

    const handleAddTipoIngreso = (nuevoTipo) => {
        if (!tiposIngreso.includes(nuevoTipo)) {
            setTiposIngreso(prev => [...prev, nuevoTipo]);
        }
    };

    const handleAdd = () => {
        cargarDatos();
    };

    const crearAnyo = async () => {
        const response = await gestionarAnyoContabilidad({ accion: 'crear_anyo', anyo: nuevoAnyo });
        if (response?.success) { setDialogAnyo(false); setAnioSeleccionado(Number(nuevoAnyo)); await cargarDatos(); }
    };

    const activarAnyo = async (anyo) => {
        const response = await gestionarAnyoContabilidad({ accion: 'activar_anyo', anyo });
        if (response?.success) await cargarDatos();
    };

    const confirmarTicketPagado = async (id, pagado = true, tipoCobro = 'ticket', pagoCompensado = false) => {
        const response = await marcarTicketPagado(id, pagado, tipoCobro, null, pagoCompensado);
        if (response?.success) { setErrorTicket(''); await cargarDatos(); }
        else setErrorTicket(response?.message || 'No s\'ha pogut actualitzar el pagament.');
    };

    const abrirPagoTicket = (ticket) => {
        if (Number(ticket.pagado) === 1) {
            confirmarTicketPagado(ticket.id, Number(ticket.pagado) !== 1, ticket.tipo_cobro);
            return;
        }
        setTicketPagoParcial(ticket);
        setImportePagoParcial(String(Number(ticket.total || 0)));
        setPagoTicketCompensado(false);
        setErrorTicket('');
    };

    const confirmarPagoTicket = async () => {
        const total = Number(ticketPagoParcial?.total || 0);
        const tipoCobro = ticketPagoParcial?.tipo_cobro === 'miembro' ? 'miembro' : 'ticket';
        const esCobroMiembro = tipoCobro === 'miembro';
        const esPagoCompensado = !esCobroMiembro && pagoTicketCompensado;
        const importe = esPagoCompensado ? total : Number(importePagoParcial);
        if (!esPagoCompensado && (!importe || importe <= 0 || importe > total)) {
            setErrorTicket('Indica un import superior a 0 i igual o inferior al tiquet.');
            return;
        }
        const response = await marcarTicketPagado(ticketPagoParcial.id, true, tipoCobro, esPagoCompensado ? null : (esCobroMiembro ? total : importe), esPagoCompensado);
        if (response?.success) {
            if (esPagoCompensado && Number(response?.data?.pago_compensado || 0) !== 1) {
                setErrorTicket('El pagament s\'ha confirmat, perÃ² no ha quedat marcat com a compensat.');
                await cargarDatos();
                return;
            }
            setTicketPagoParcial(null);
            setPagoTicketCompensado(false);
            setErrorTicket('');
            await cargarDatos();
        } else setErrorTicket(response?.message || 'No s\'ha pogut actualitzar el pagament.');
    };

    const abrirDescompensarTicket = (event, ticket) => {
        event.stopPropagation();
        setTicketDescompensar(ticket);
        setErrorTicket('');
    };

    const cerrarDescompensarTicket = () => {
        if (descompensandoTicket) return;
        setTicketDescompensar(null);
    };

    const confirmarDescompensarTicket = async () => {
        if (!ticketDescompensar) return;
        setDescompensandoTicket(true);
        const response = await marcarTicketPagado(ticketDescompensar.id, false, ticketDescompensar.tipo_cobro, null, false);
        if (response?.success) {
            setTicketDescompensar(null);
            setErrorTicket('');
            await cargarDatos();
        } else {
            setErrorTicket(response?.message || 'No s\'ha pogut descompensar el pagament.');
        }
        setDescompensandoTicket(false);
    };

    const ticketsFiltrados = ticketsContables.filter((ticket) => {
        const fecha = String(ticket.fecha || '').slice(0, 10);
        return (!filtroTicketMiembro || String(ticket.id_usuario) === filtroTicketMiembro)
            && (!filtroTicketEvento || String(ticket.evento) === filtroTicketEvento)
            && (!filtroTicketConcepto || String(ticket.concepto_contable || '') === filtroTicketConcepto)
            && (
                filtroTicketEstado === 'tots'
                || (filtroTicketEstado === 'pagats' && Number(ticket.pagado) === 1 && Number(ticket.pago_compensado || 0) !== 1)
                || (filtroTicketEstado === 'compensats' && Number(ticket.pago_compensado || 0) === 1)
                || (filtroTicketEstado === 'pendents' && Number(ticket.pagado) !== 1)
            )
            && (!filtroTicketDesde || fecha >= filtroTicketDesde)
            && (!filtroTicketHasta || fecha <= filtroTicketHasta);
    }).sort((a, b) => {
        const campos = { usuario: 'usuario', evento: 'evento', concepto: 'concepto_contable' };
        const campo = campos[vistaTickets] || 'usuario';
        return String(a[campo] || '').localeCompare(String(b[campo] || ''), 'ca');
    });
    const miembrosTickets = [...new Map(ticketsContables.map((ticket) => [ticket.id_usuario, ticket.usuario])).entries()];
    const eventosTickets = [...new Set(ticketsContables.map((ticket) => ticket.evento))];
    const conceptosTickets = [...new Set(ticketsContables.map((ticket) => ticket.concepto_contable || 'Sense assignar'))];
    const totalizarTickets = (tickets) => tickets.reduce((total, ticket) => {
        const importe = Number(ticket.total || 0);
        total.total += importe;
        if (Number(ticket.pago_compensado || 0) === 1) total.compensado += importe;
        else if (Number(ticket.pagado) === 1) total.pagado += importe;
        else total.pendiente += importe;
        return total;
    }, { pagado: 0, pendiente: 0, compensado: 0, total: 0 });
    const totalesTickets = totalizarTickets(ticketsFiltrados);
    const totalesTicketsAnyo = totalizarTickets(ticketsContables);

    const abrirEditarConcepto = (event, apartado) => {
        event.stopPropagation();
        setConceptoEditar(apartado);
        setNombreConcepto(apartado.apartado);
        setConceptoActivo(Number(apartado.activo) === 1);
        setErrorConcepto('');
    };

    const guardarConcepto = async () => {
        const nombreHaCambiado = nombreConcepto.trim() !== String(conceptoEditar?.apartado || '').trim();
        const estadoHaCambiado = conceptoActivo !== (Number(conceptoEditar?.activo) === 1);
        if (!nombreHaCambiado && !estadoHaCambiado) {
            setConceptoEditar(null);
            return;
        }

        if (nombreHaCambiado) {
            const responseNombre = await editarConceptoContabilidad(conceptoEditar.id_apartado, nombreConcepto);
            if (!responseNombre?.success) {
                setErrorConcepto(responseNombre?.message || 'No s\'ha pogut editar el concepte.');
                return;
            }
        }

        if (estadoHaCambiado) {
            const responseEstado = await actualizarEstadoConceptoContabilidad(conceptoEditar.id_apartado, conceptoActivo);
            if (!responseEstado?.success) {
                setErrorConcepto(responseEstado?.message || 'No s\'ha pogut actualitzar l\'estat.');
                return;
            }
        }

        setConceptoEditar(null);
        await cargarDatos();
    };

    const confirmarEliminarConcepto = async () => {
        const response = await eliminarConceptoContabilidad(conceptoEliminar.id_apartado);
        if (response?.success) { setConceptoEliminar(null); await cargarDatos(); }
        else setErrorConcepto(response?.message || 'No s\'ha pogut eliminar el concepte.');
    };

    const handleCellClick = (usuario, id_usuario, tipo, id_tipo, cantidad = 0, ingreso = null, id_apartado = null) => {
        if (!puedeRegistrarIngresos) return;
        setCurrenApartado(id_apartado);
        setIngresoSeleccionado({
            id_ingreso: ingreso?.id_ingreso || null,
            nombre_usuario: usuario,
            tipo_ingreso: tipo,
            id_usuario: id_usuario,
            id_tipo_ingreso: id_tipo,
            cantidad,
            id_concepto_contabilidad: ingreso?.id_concepto_contabilidad || '',
            id_evento: ingreso?.id_evento || null,
            tipo_cobro: ingreso?.tipo_cobro || '',
            ticket_pendiente: Boolean(ingreso?.ticket_pendiente),
            es_ingreso_ticket: Boolean(ingreso?.es_ingreso_ticket),
            puede_eliminar: ingreso?.puede_eliminar || 0
        });
        setModalEditOpen(true);
    };

    const abrirEliminarGasto = (gasto, tipoGasto) => {
        setGastoEliminar({ ...gasto, tipo_gasto: tipoGasto });
        setErrorEliminarGasto('');
    };

    const abrirMarcarPagado = (event, gasto, tipoGasto) => {
        event.stopPropagation();
        setGastoPagado({ ...gasto, tipo_gasto: tipoGasto });
        setErrorMarcarPagado('');
    };

    const abrirEditarGasto = (gasto, tipoGasto) => {
        if (Number(gasto?.puede_eliminar) !== 1) {
            return;
        }

        setCurrenApartado(gasto.id_apartado || currenApartado);
        setGastoSeleccionado({ ...gasto, tipo_gasto: tipoGasto });
        setModalEditarGastoOpen(true);
    };

    const confirmarEliminarGasto = async () => {
        if (!gastoEliminar?.id_gasto) {
            return;
        }

        setEliminandoGasto(true);
        setErrorEliminarGasto('');

        try {
            const response = await deleteGasto({ id_gasto: gastoEliminar.id_gasto });
            if (response?.success) {
                setGastoEliminar(null);
                await cargarDatos();
                return;
            }

            setErrorEliminarGasto(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            console.error('Error en eliminar la despesa:', error);
            setErrorEliminarGasto('Ha sorgit un error.');
        } finally {
            setEliminandoGasto(false);
        }
    };

    const abrirEliminarIngreso = (event, ingreso, usuario, tipoIngreso) => {
        event.stopPropagation();
        if (!ingreso?.id_ingreso) {
            return;
        }

        setIngresoEliminar({
            ...ingreso,
            nombre_usuario: usuario.nombre_usuario,
            tipo_ingreso: tipoIngreso.tipo_ingreso,
            es_ingreso_ticket: Boolean(ingreso.es_ingreso_ticket)
        });
        setErrorEliminarIngreso('');
    };

    const confirmarEliminarIngreso = async () => {
        if (!ingresoEliminar?.id_ingreso) {
            return;
        }

        setEliminandoIngreso(true);
        setErrorEliminarIngreso('');

        try {
            const response = await deleteIngreso({ id_ingreso: ingresoEliminar.id_ingreso });
            if (response?.success) {
                setIngresoEliminar(null);
                await cargarDatos();
                return;
            }

            setErrorEliminarIngreso(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            console.error("Error en eliminar l'ingrés:", error);
            setErrorEliminarIngreso('Ha sorgit un error.');
        } finally {
            setEliminandoIngreso(false);
        }
    };

    const confirmarMarcarPagado = async () => {
        if (!gastoPagado?.id_gasto) {
            return;
        }

        setMarcandoPagado(true);
        setErrorMarcarPagado('');

        try {
            const response = await marcarGastoPagado({ id_gasto: gastoPagado.id_gasto });
            if (response?.success) {
                setGastoPagado(null);
                await cargarDatos();
                return;
            }

            setErrorMarcarPagado(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            console.error('Error en marcar la despesa com a pagada:', error);
            setErrorMarcarPagado('Ha sorgit un error.');
        } finally {
            setMarcandoPagado(false);
        }
    };

    const abrirMarcarIngresoPagado = (event, ingreso) => {
        event.stopPropagation();
        setIngresoPagado(ingreso);
        setImporteIngresoPagado(String(Number(ingreso.cantidad || 0)));
        setErrorIngresoPagado('');
    };

    const confirmarMarcarIngresoPagado = async () => {
        if (!ingresoPagado?.id_ingreso) return;
        setMarcandoIngresoPagado(true);
        setErrorIngresoPagado('');
        const importe = Number(importeIngresoPagado);
        const total = Number(ingresoPagado.cantidad || 0);
        if (!importe || importe <= 0 || importe > total) {
            setErrorIngresoPagado('Indica un import superior a 0 i igual o inferior a l\'ingrés.');
            return;
        }
        try {
            const response = await marcarIngresoPagado({ id_ingreso: ingresoPagado.id_ingreso, importe_pagado: importe });
            if (response?.success) {
                setIngresoPagado(null);
                await cargarDatos();
                return;
            }
            setErrorIngresoPagado(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            setErrorIngresoPagado('Ha sorgit un error.');
        } finally {
            setMarcandoIngresoPagado(false);
        }
    };

    const abrirDesmarcarIngresoPagado = (event, ingreso, usuario, tipoIngreso) => {
        event.stopPropagation();
        if (!ingreso?.id_ingreso) {
            return;
        }

        setIngresoDesmarcarPagado({
            ...ingreso,
            nombre_usuario: usuario?.nombre_usuario || ingreso.nombre_usuario || 'General',
            tipo_ingreso: tipoIngreso?.tipo_ingreso || ingreso.tipo_ingreso || ''
        });
        setErrorDesmarcarIngresoPagado('');
    };

    const cerrarDesmarcarIngresoPagado = () => {
        if (desmarcandoIngresoPagado) return;
        setIngresoDesmarcarPagado(null);
        setErrorDesmarcarIngresoPagado('');
    };

    const confirmarDesmarcarIngresoPagado = async () => {
        if (!ingresoDesmarcarPagado?.id_ingreso) return;
        setDesmarcandoIngresoPagado(true);
        setErrorDesmarcarIngresoPagado('');

        try {
            const esIngresoTicket = Boolean(ingresoDesmarcarPagado.es_ingreso_ticket);
            const idCobroTicket = ingresoDesmarcarPagado.id_asistente_evento || ingresoDesmarcarPagado.id_ticket_consumido;
            const tipoCobroTicket = ingresoDesmarcarPagado.tipo_cobro || (ingresoDesmarcarPagado.id_asistente_evento ? 'miembro' : 'ticket');
            if (esIngresoTicket && !idCobroTicket) {
                setErrorDesmarcarIngresoPagado('No s\'ha trobat la refer&egrave;ncia del tiquet.');
                return;
            }
            const response = esIngresoTicket
                ? await marcarTicketPagado(idCobroTicket, false, tipoCobroTicket, null, false)
                : await marcarIngresoPagado({ id_ingreso: ingresoDesmarcarPagado.id_ingreso, pagado: 0 });
            if (response?.success) {
                setIngresoDesmarcarPagado(null);
                await cargarDatos();
                return;
            }
            setErrorDesmarcarIngresoPagado(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            setErrorDesmarcarIngresoPagado('Ha sorgit un error.');
        } finally {
            setDesmarcandoIngresoPagado(false);
        }
    };

    const calcularTotalIngresos = (apartado) => {
        return (apartado.tipos_ingresos ?? []).reduce((acc, tipo) => {
            return acc + (tipo.ingresos ?? []).reduce((sum, ingreso) => {
                const cantidad = parseFloat(ingreso.cantidad);
                return sum + (isNaN(cantidad) ? 0 : cantidad);
            }, 0);
        }, 0);
    };

    const calcularTotalGastos = (apartado) => {
        return (apartado.tipos_gastos ?? []).reduce((acc, tipo) => {
            return acc + (tipo.gastos ?? []).reduce((sum, gasto) => {
                const cantidad = parseFloat(gasto.cantidad);
                return sum + (isNaN(cantidad) ? 0 : cantidad);
            }, 0);
        }, 0);
    };

    const obtenerIngresosUsuarioApartado = (apartado, idUsuario) => {
        return (apartado.tipos_ingresos ?? []).flatMap((tipo) =>
            (tipo.ingresos ?? [])
                .filter((ingreso) => String(ingreso.id_usuario) === String(idUsuario))
                .map((ingreso) => ({
                    ...ingreso,
                    tipo_ingreso: tipo.tipo_ingreso,
                    id_tipo_ingreso: tipo.id_tipo_ingreso,
                    id_tipo_dato: tipo.id_tipo_dato
                }))
        );
    };

    const obtenerIngresosGeneralesApartado = (apartado) => {
        return (apartado.tipos_ingresos ?? []).flatMap((tipo) =>
            (tipo.ingresos ?? [])
                .filter((ingreso) => Number(ingreso.ingreso_general) === 1 || ingreso.id_usuario === null)
                .map((ingreso) => ({
                    ...ingreso,
                    tipo_ingreso: tipo.tipo_ingreso,
                    id_tipo_ingreso: tipo.id_tipo_ingreso,
                    id_tipo_dato: tipo.id_tipo_dato
                }))
        );
    };

    const calcularTotalIngresosUsuarioApartado = (apartado, idUsuario) => {
        return obtenerIngresosUsuarioApartado(apartado, idUsuario).reduce((total, ingreso) => {
            const cantidad = parseFloat(ingreso.cantidad);
            return total + (isNaN(cantidad) ? 0 : cantidad);
        }, 0);
    };

    const calcularTotalIngresosUsuario = (idUsuario) => {
        return datosContables.reduce((total, apartado) => {
            return total + calcularTotalIngresosUsuarioApartado(apartado, idUsuario);
        }, 0);
    };

    const calcularTotalIngresosGeneralesApartado = (apartado) => {
        return obtenerIngresosGeneralesApartado(apartado).reduce((total, ingreso) => {
            const cantidad = parseFloat(ingreso.cantidad);
            return total + (isNaN(cantidad) ? 0 : cantidad);
        }, 0);
    };

    const calcularTotalIngresosGenerales = () => {
        return datosContables.reduce((total, apartado) => {
            return total + calcularTotalIngresosGeneralesApartado(apartado);
        }, 0);
    };

    const ordenarIngresosRecientes = (ingresos) => {
        return [...ingresos].sort((a, b) => {
            const fechaA = new Date(String(a.fecha_creacion || '').replace(' ', 'T')).getTime() || 0;
            const fechaB = new Date(String(b.fecha_creacion || '').replace(' ', 'T')).getTime() || 0;

            if (fechaB !== fechaA) {
                return fechaB - fechaA;
            }

            return Number(b.id_ingreso || 0) - Number(a.id_ingreso || 0);
        });
    };

    const togglePagosDesplegados = (cellKey) => {
        setPagosDesplegados((prev) => ({
            ...prev,
            [cellKey]: !prev[cellKey],
        }));
    };

    const toggleConceptoUsuario = (key) => {
        setConceptoUsuariosDesplegados((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const obtenerNombreUsuarioMovimiento = (movimiento, tipoMovimiento) => {
        const esGeneral = Number(movimiento.ingreso_general) === 1 || movimiento.id_usuario === null;
        if (esGeneral && tipoMovimiento === 'ingreso') return 'General';
        if (movimiento.nombre_usuario) return movimiento.nombre_usuario;

        const usuarioMovimiento = usuarios.find((usuario) => String(usuario.id_usuario) === String(movimiento.id_usuario));
        return usuarioMovimiento?.nombre_usuario || 'Usuari';
    };

    const agruparMovimientosPorUsuario = (movimientos, tipoMovimiento) => {
        const grupos = new Map();

        movimientos.forEach((movimiento) => {
            const nombreUsuario = obtenerNombreUsuarioMovimiento(movimiento, tipoMovimiento);
            const idUsuario = movimiento.id_usuario ?? (nombreUsuario === 'General' ? 'general' : nombreUsuario);
            const key = String(idUsuario);
            const importe = Number(movimiento.cantidad || 0);
            const confirmado = Number(movimiento.pagado) === 1;

            if (!grupos.has(key)) {
                grupos.set(key, {
                    key,
                    nombre: nombreUsuario,
                    confirmado: 0,
                    pendiente: 0,
                    total: 0,
                    registros: []
                });
            }

            const grupo = grupos.get(key);
            grupo.total += importe;
            if (confirmado) grupo.confirmado += importe;
            else grupo.pendiente += importe;
            grupo.registros.push(movimiento);
        });

        return Array.from(grupos.values())
            .map((grupo) => ({
                ...grupo,
                registros: grupo.registros.sort((a, b) => String(b.fecha_creacion || '').localeCompare(String(a.fecha_creacion || '')))
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'ca'));
    };

    const renderMovimientosConceptoPorUsuario = (concepto, movimientos, tipoMovimiento, scopeKey) => {
        const grupos = agruparMovimientosPorUsuario(movimientos, tipoMovimiento);
        const esGasto = tipoMovimiento === 'gasto';

        if (!grupos.length) {
            return (
                <TableRow>
                    <TableCell colSpan={4} className="accounting-empty-cell">
                        {esGasto ? 'Sense despeses.' : 'Sense ingressos.'}
                    </TableCell>
                </TableRow>
            );
        }

        return grupos.map((grupo) => {
            const rowKey = `${scopeKey || 'apartado'}-${concepto.id || 'no-definit'}-${tipoMovimiento}-${grupo.key}`;
            const abierto = Boolean(conceptoUsuariosDesplegados[rowKey]);

            return (
                <React.Fragment key={rowKey}>
                    <TableRow className="accounting-concept-user-row" onClick={() => toggleConceptoUsuario(rowKey)}>
                        <TableCell colSpan={4}>
                            <Box className="accounting-concept-user-summary">
                                <Box>
                                    <Typography className="accounting-concept-user-name">{grupo.nombre}</Typography>
                                    <Typography className="accounting-caption">
                                        {grupo.registros.length} {grupo.registros.length === 1 ? 'moviment' : 'moviments'}
                                    </Typography>
                                </Box>
                                <Box className="accounting-concept-user-totals">
                                    <span className="accounting-confirmed-text">
                                        Confirmat: {esGasto ? formatoGasto(grupo.confirmado) : formatoEuro(grupo.confirmado)}
                                    </span>
                                    <span className="accounting-pending-text">
                                        Pendent: {esGasto ? formatoGasto(grupo.pendiente) : formatoEuro(grupo.pendiente)}
                                    </span>
                                    <strong>
                                        Total: {esGasto ? formatoGasto(grupo.total) : formatoEuro(grupo.total)}
                                    </strong>
                                    <IconButton size="small" className={abierto ? 'accounting-rotate-icon accounting-rotate-icon-open' : 'accounting-rotate-icon'}>
                                        <ExpandMoreIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </TableCell>
                    </TableRow>

                    <TableRow className="accounting-concept-user-detail-row">
                        <TableCell colSpan={4}>
                            <Collapse in={abierto} timeout="auto" unmountOnExit>
                                <Box className="accounting-concept-user-detail">
                                    <Table size="small" className="accounting-table accounting-concept-movement-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Data</TableCell>
                                                <TableCell>Moviment</TableCell>
                                                <TableCell>Estat</TableCell>
                                                <TableCell align="right">Import</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {grupo.registros.map((movimiento, index) => {
                                                const confirmado = Number(movimiento.pagado) === 1;
                                                const rowClass = confirmado ? 'accounting-confirmed-row' : 'accounting-unpaid-row';

                                                return (
                                                    <TableRow key={movimiento.id_ingreso || movimiento.id_gasto || movimiento.id || `${rowKey}-${index}`} className={rowClass}>
                                                        <TableCell className="accounting-concept-child-cell">{formatoFecha(movimiento.fecha_creacion) || '-'}</TableCell>
                                                        <TableCell>
                                                            <strong>{esGasto ? movimiento.tipo_gasto || '-' : movimiento.tipo_ingreso || '-'}</strong>
                                                            {esGasto && movimiento.descripcion ? (
                                                                <Typography className="accounting-caption">{movimiento.descripcion}</Typography>
                                                            ) : ''}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className={confirmado ? 'accounting-confirmed-text' : 'accounting-pending-text'}>
                                                                {confirmado ? 'Confirmat' : 'Pendent'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="right" className="accounting-money">
                                                            {esGasto ? formatoGasto(movimiento.cantidad) : formatoEuro(movimiento.cantidad)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                </React.Fragment>
            );
        });
    };

    const renderPagosCelda = (ingresos, cellKey) => {
        const ingresosOrdenados = ordenarIngresosRecientes(ingresos);
        const desplegado = Boolean(pagosDesplegados[cellKey]);
        const ingresosVisibles = desplegado ? ingresosOrdenados : ingresosOrdenados.slice(0, 3);
        const pagosOcultos = Math.max(ingresosOrdenados.length - 3, 0);

        if (ingresosOrdenados.length === 0) {
            return (
                <Typography className="accounting-user-ledger-empty-cell">
                    Sense pagaments
                </Typography>
            );
        }

        return (
            <Stack className="accounting-user-ledger-payments">
                {ingresosVisibles.map((ingreso) => (
                    <Box
                        key={ingreso.id_ingreso || `${ingreso.id_tipo_ingreso}-${ingreso.cantidad}`}
                        className="accounting-user-ledger-payment"
                    >
                        <Typography>
                            {ingreso.tipo_ingreso}
                        </Typography>
                        <strong>{formatoEuro(ingreso.cantidad)}</strong>

                        {formatoFecha(ingreso.fecha_creacion) ? (
                            <Typography className="accounting-user-ledger-payment-date">
                                {formatoFecha(ingreso.fecha_creacion)}
                            </Typography>
                        ) : ''}
                    </Box>
                ))}
                {pagosOcultos > 0 ? (
                    <Button
                        size="small"
                        variant="text"
                        className="accounting-user-ledger-more"
                        onClick={() => togglePagosDesplegados(cellKey)}
                    >
                        {desplegado ? 'Mostrar menys' : `Veure ${pagosOcultos} més`}
                    </Button>
                ) : ''}
            </Stack>
        );
    };

    const abrirPagoUsuarioApartado = (usuario, apartado) => {
        if (!puedeRegistrarIngresos) return;
        setPagoGeneralApartado(null);
        setCurrenApartado(apartado.id_apartado);
        setPagoUsuarioApartado({
            usuario,
            apartado
        });
        setModalTipoIngresoOpen(true);
    };

    const abrirPagoGeneralApartado = (apartado) => {
        if (!puedeRegistrarIngresos) return;
        setPagoUsuarioApartado(null);
        setCurrenApartado(apartado.id_apartado);
        setPagoGeneralApartado(apartado);
        setModalTipoIngresoOpen(true);
    };

    const getApartadoActual = () => datosContables.find((apartado) => String(apartado.id_apartado) === String(currenApartado));
    const getConceptosApartadoActual = () => getApartadoActual()?.conceptos_predefinidos ?? [];

    const getResumenConceptosApartado = (apartado) => {
        const conceptos = new Map();

        (apartado.conceptos_predefinidos ?? []).forEach((concepto) => {
            conceptos.set(String(concepto.id), {
                id: concepto.id,
                nombre: concepto.nombre,
                ingresos: 0,
                ingresosConfirmados: 0,
                ingresosPendientes: 0,
                gastos: 0,
                gastosConfirmados: 0,
                gastosPendientes: 0,
                cantidadIngresos: 0,
                cantidadGastos: 0,
                registrosIngresos: [],
                registrosGastos: []
            });
        });

        const getRegistro = (id, nombre) => {
            const key = id ? String(id) : 'no-definit';
            if (!conceptos.has(key)) {
                conceptos.set(key, {
                    id: id || null,
                    nombre: nombre || 'No definit',
                    ingresos: 0,
                    ingresosConfirmados: 0,
                    ingresosPendientes: 0,
                    gastos: 0,
                    gastosConfirmados: 0,
                    gastosPendientes: 0,
                    cantidadIngresos: 0,
                    cantidadGastos: 0,
                    registrosIngresos: [],
                    registrosGastos: []
                });
            }
            return conceptos.get(key);
        };

        (apartado.tipos_ingresos ?? []).forEach((tipo) => {
            (tipo.ingresos ?? []).forEach((ingreso) => {
                const registro = getRegistro(ingreso.id_concepto_contabilidad, ingreso.nombre_concepto_contabilidad);
                const cantidad = Number(ingreso.cantidad || 0);
                registro.ingresos += cantidad;
                if (Number(ingreso.pagado) === 1) {
                    registro.ingresosConfirmados += cantidad;
                } else {
                    registro.ingresosPendientes += cantidad;
                }
                registro.cantidadIngresos += 1;
                registro.registrosIngresos.push({
                    ...ingreso,
                    tipo_ingreso: tipo.tipo_ingreso,
                    id_tipo_ingreso: tipo.id_tipo_ingreso,
                    id_tipo_dato: tipo.id_tipo_dato
                });
            });
        });

        ticketsContables
            .filter((ticket) => String(ticket.concepto_contable || '') === String(apartado.apartado) && Number(ticket.pagado) !== 1)
            .forEach((ticket) => {
                const registro = getRegistro(ticket.id_concepto_contabilidad, ticket.nombre_concepto_contabilidad);
                const cantidad = Number(ticket.total || 0);
                registro.ingresos += cantidad;
                registro.ingresosPendientes += cantidad;
                registro.cantidadIngresos += 1;
                registro.registrosIngresos.push({
                    id_ingreso: null,
                    id: ticket.id,
                    id_evento: ticket.id_evento || null,
                    tipo_cobro: ticket.tipo_cobro || 'ticket',
                    id_usuario: ticket.id_usuario || null,
                    ingreso_general: 1,
                    evento: ticket.evento,
                    ticket: ticket.ticket,
                    tipo_ingreso: `${ticket.evento} â€” ${ticket.ticket} Ã— ${ticket.cantidad}`,
                    id_concepto_contabilidad: ticket.id_concepto_contabilidad || null,
                    nombre_concepto_contabilidad: ticket.nombre_concepto_contabilidad || 'No definit',
                    cantidad: ticket.total,
                    total: ticket.total,
                    fecha_creacion: ticket.fecha,
                    pagado: 0,
                    ticket_pendiente: true,
                    es_ingreso_ticket: true
                });
            });

        (apartado.tipos_gastos ?? []).forEach((tipo) => {
            (tipo.gastos ?? []).forEach((gasto) => {
                const registro = getRegistro(gasto.id_concepto_contabilidad, gasto.nombre_concepto_contabilidad);
                const cantidad = Number(gasto.cantidad || 0);
                registro.gastos += cantidad;
                if (Number(gasto.pagado) === 1) {
                    registro.gastosConfirmados += cantidad;
                } else {
                    registro.gastosPendientes += cantidad;
                }
                registro.cantidadGastos += 1;
                registro.registrosGastos.push({
                    ...gasto,
                    tipo_gasto: tipo.tipo_gasto
                });
            });
        });

        return Array.from(conceptos.values()).sort((a, b) => {
            if (a.id === null) return 1;
            if (b.id === null) return -1;
            return String(a.nombre).localeCompare(String(b.nombre), 'ca');
        });
    };

    const abrirCrearConceptoPredefinido = (apartado) => {
        if (!puedeGestionarConceptosPredefinidos) return;
        setConceptoPredefinidoDialog({ apartado, concepto: null });
        setNombreConceptoPredefinido('');
        setApartadoConceptoPredefinidoId(String(apartado.id_apartado || ''));
        setErrorConceptoPredefinido('');
    };

    const abrirEditarConceptoPredefinido = (apartado, concepto) => {
        if (!puedeGestionarConceptosPredefinidos) return;
        setConceptoPredefinidoDialog({ apartado, concepto });
        setNombreConceptoPredefinido(concepto.nombre || '');
        setApartadoConceptoPredefinidoId(String(apartado.id_apartado || ''));
        setErrorConceptoPredefinido('');
    };

    const cerrarConceptoPredefinidoDialog = () => {
        if (guardandoConceptoPredefinido) return;
        setConceptoPredefinidoDialog(null);
        setNombreConceptoPredefinido('');
        setApartadoConceptoPredefinidoId('');
        setErrorConceptoPredefinido('');
    };

    const guardarConceptoPredefinido = async () => {
        const nombre = nombreConceptoPredefinido.trim();
        const apartado = conceptoPredefinidoDialog?.apartado;
        const concepto = conceptoPredefinidoDialog?.concepto;
        const idApartadoDestino = Number(apartadoConceptoPredefinidoId || apartado?.id_apartado || 0);

        if (!idApartadoDestino || !nombre) {
            setErrorConceptoPredefinido('Indica el nom del concepte.');
            return;
        }

        setGuardandoConceptoPredefinido(true);
        setErrorConceptoPredefinido('');

        try {
            const response = concepto?.id
                ? await editarConceptoPredefinidoContabilidad({ id: concepto.id, id_apartado: idApartadoDestino, nombre })
                : await crearConceptoPredefinidoContabilidad({ id_apartado: idApartadoDestino, nombre });

            if (response?.success) {
                cerrarConceptoPredefinidoDialog();
                await cargarDatos();
                return;
            }

            setErrorConceptoPredefinido(response?.message || 'Ha sorgit un error.');
        } catch (error) {
            console.error('Error en guardar el concepte predefinit:', error);
            setErrorConceptoPredefinido('Ha sorgit un error.');
        } finally {
            setGuardandoConceptoPredefinido(false);
        }
    };

    const confirmarEliminarConceptoPredefinido = async () => {
        if (!conceptoPredefinidoEliminar?.concepto?.id || !conceptoPredefinidoEliminar?.apartado?.id_apartado) return;

        const response = await eliminarConceptoPredefinidoContabilidad({
            id: conceptoPredefinidoEliminar.concepto.id,
            id_apartado: conceptoPredefinidoEliminar.apartado.id_apartado
        });

        if (response?.success) {
            setConceptoPredefinidoEliminar(null);
            await cargarDatos();
        }
    };

    const totalIngresos = datosContables.reduce((acc, apartado) => acc + calcularTotalIngresos(apartado), 0);
    const totalGastos = datosContables.reduce((acc, apartado) => acc + calcularTotalGastos(apartado), 0);
    const ingresosManuales = datosContables.flatMap((apartado) => (apartado.tipos_ingresos ?? []).flatMap((tipo) => (tipo.ingresos ?? []).map((ingreso) => ({ ...ingreso, tipo_ingreso: tipo.tipo_ingreso }))));
    const ingresosManualesProvisionales = ingresosManuales.filter((ingreso) => Number(ingreso.pagado) !== 1);
    const ingresosPendientes = ingresosManualesProvisionales.sort((a, b) => String(b.fecha_creacion || '').localeCompare(String(a.fecha_creacion || '')));
    const totalIngresosProvisionales = ingresosPendientes.reduce((total, ingreso) => total + Number(ingreso.cantidad || 0), 0) + totalizarTickets(ticketsContables).pendiente;
    const totalIngresosConfirmados = totalIngresos - ingresosManualesProvisionales.reduce((total, ingreso) => total + Number(ingreso.cantidad || 0), 0);
    const gastosPendientes = datosContables.flatMap((apartado) => (apartado.tipos_gastos ?? []).flatMap((tipo) => (tipo.gastos ?? []).map((gasto) => ({ ...gasto, tipo_gasto: tipo.tipo_gasto })))).filter((gasto) => Number(gasto.pagado) !== 1).sort((a, b) => Number(b.id_gasto) - Number(a.id_gasto));
    const totalGastosProvisionales = gastosPendientes.reduce((total, gasto) => total + Number(gasto.cantidad || 0), 0);
    const totalGastosConfirmados = totalGastos - totalGastosProvisionales;
    const totalIngresosPendientesTabla = ingresosManualesProvisionales.reduce((total, ingreso) => total + Number(ingreso.cantidad || 0), 0);
    const totalIngresosConfirmadosTabla = ingresosManuales.reduce((total, ingreso) => Number(ingreso.pagado) === 1 ? total + Number(ingreso.cantidad || 0) : total, 0);
    const totalGastosPendientesTabla = totalGastosProvisionales;
    const totalGastosConfirmadosTabla = totalGastosConfirmados;
    const pagosCompensados = ticketsContables
        .filter((ticket) => Number(ticket.pago_compensado || 0) === 1)
        .sort((a, b) => String(a.usuario || '').localeCompare(String(b.usuario || ''), 'ca') || String(b.fecha || '').localeCompare(String(a.fecha || '')));
    const totalPagosCompensados = pagosCompensados.reduce((total, ticket) => total + Number(ticket.total || 0), 0);
    const pagosCompensadosPorPersona = Object.values(pagosCompensados.reduce((grupos, ticket) => {
        const key = String(ticket.id_usuario || ticket.usuario || 'sense-usuari');
        if (!grupos[key]) {
            grupos[key] = {
                key,
                usuario: ticket.usuario || 'Usuari',
                total: 0,
                tickets: []
            };
        }
        grupos[key].total += Number(ticket.total || 0);
        grupos[key].tickets.push(ticket);
        return grupos;
    }, {}));
    const totalIngresosFinal = totalIngresosProvisionales + totalIngresosConfirmados;
    const totalGastosFinal = totalGastosProvisionales + totalGastosConfirmados;
    const balanceProvisional = totalIngresosProvisionales - totalGastosProvisionales;
    const balanceConfirmado = totalIngresosConfirmados - totalGastosConfirmados;
    const balanceFinal = balanceProvisional + balanceConfirmado;

    const abrirRegistros = (event, id) => {
        event.stopPropagation();
        setDialogRegistros(true);
        setApartado(id);
    };

    return (
        <Box className="accounting-shell">
            <Paper elevation={0} className="accounting-header">
                <Box className="accounting-header-copy">
                    <Typography className="accounting-eyebrow">
                        Comptabilitat
                    </Typography>
                    <Typography variant="h4" className="accounting-title">
                        Resum econòmic
                    </Typography>
                </Box>

                <Stack direction="row" className="accounting-toolbar">
                    <TextField
                        select
                        label="Any"
                        value={anioSeleccionado}
                        onChange={(e) => setAnioSeleccionado(Number(e.target.value))}
                        size="small"
                        className="accounting-year-field"
                    >
                        {listaAnios.map((anio) => (
                            <MenuItem key={anio} value={anio}>
                                {anio}
                            </MenuItem>
                        ))}
                    </TextField>
                    {puedeRegistrarIngresos && <Button variant="outlined" onClick={() => setDialogAnyo(true)}>Any nou</Button>}
                    {puedeRegistrarIngresos && <Button variant="outlined" onClick={() => activarAnyo(anioSeleccionado)}>Fer any actiu</Button>}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setModalAddOpen(true)}
                        disabled={opciones != 1}
                    >
                        Apartat
                    </Button>
                </Stack>
            </Paper>

            <Grid container spacing={2} className="accounting-kpis">
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper elevation={0} className="accounting-kpi accounting-kpi-income" sx={{ bgcolor: 'var(--verde)' }}>
                        <Box>
                            <Grid
                                container
                                direction="row"
                                size={{ xs: 12 }}
                                sx={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <TrendingUpIcon />
                                <Typography className="accounting-kpi-label">
                                    Ingressos
                                </Typography>
                            </Grid>

                            <ResumenImportes confirmado={totalIngresosConfirmados} pendiente={totalIngresosProvisionales} total={totalIngresosFinal} />
                        </Box>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper elevation={0} className="accounting-kpi accounting-kpi-expense" sx={{ bgcolor: 'var(--rojo)' }}>
                        <Box>
                            <Grid
                                container
                                direction="row"
                                size={{ xs: 12 }}
                                sx={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <TrendingDownIcon />
                                <Typography className="accounting-kpi-label">
                                    Despeses
                                </Typography>
                            </Grid>
                            <ResumenImportes confirmado={totalGastosConfirmados} pendiente={totalGastosProvisionales} total={totalGastosFinal} esGasto />
                        </Box>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Paper sx={{ bgcolor: 'var(--azul)' }} elevation={0} className={`accounting-kpi accounting-kpi-balance ${balanceFinal >= 0 ? 'accounting-kpi-positive' : 'accounting-kpi-negative'}`}>
                        <Box>
                            <Grid
                                container
                                direction="row"
                                size={{ xs: 12 }}
                                sx={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <AccountBalanceWalletIcon />
                                <Typography className="accounting-kpi-label">
                                    {'Balan\u00e7'}
                                </Typography>
                            </Grid>
                            <ResumenImportes confirmado={balanceConfirmado} total={balanceFinal} incluirPendiente={false} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {(
                <Accordion className="accounting-user-ledger" elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <CabeceraListaContable titulo={'Gesti\u00f3 de tiquets'} cantidad={ticketsFiltrados.length} confirmado={totalesTicketsAnyo.pagado} pendiente={totalesTicketsAnyo.pendiente} total={totalesTicketsAnyo.total} compensado={totalesTicketsAnyo.compensado} etiqueta={'Pagat'} />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField select fullWidth size="small" label="Membre" value={filtroTicketMiembro} onChange={(event) => setFiltroTicketMiembro(event.target.value)}><MenuItem value="">Tots</MenuItem>{miembrosTickets.map(([id, nombre]) => <MenuItem key={id} value={String(id)}>{nombre}</MenuItem>)}</TextField></Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField select fullWidth size="small" label="Esdeveniment" value={filtroTicketEvento} onChange={(event) => setFiltroTicketEvento(event.target.value)}><MenuItem value="">Tots</MenuItem>{eventosTickets.map((evento) => <MenuItem key={evento} value={evento}>{evento}</MenuItem>)}</TextField></Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 2 }}><TextField select fullWidth size="small" label="Estat" value={filtroTicketEstado} onChange={(event) => setFiltroTicketEstado(event.target.value)}><MenuItem value="tots">Tots</MenuItem><MenuItem value="pendents">Pendents</MenuItem><MenuItem value="pagats">Pagats</MenuItem><MenuItem value="compensats">Compensats</MenuItem></TextField></Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField select fullWidth size="small" label="Concepte comptable" value={filtroTicketConcepto} onChange={(event) => setFiltroTicketConcepto(event.target.value)}><MenuItem value="">Tots</MenuItem>{conceptosTickets.map((concepte) => <MenuItem key={concepte} value={concepte}>{concepte}</MenuItem>)}</TextField></Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 2 }}><TextField fullWidth size="small" type="date" label="Des de" slotProps={{ inputLabel: { shrink: true } }} value={filtroTicketDesde} onChange={(event) => setFiltroTicketDesde(event.target.value)} /></Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 2 }}><TextField fullWidth size="small" type="date" label="Fins a" slotProps={{ inputLabel: { shrink: true } }} value={filtroTicketHasta} onChange={(event) => setFiltroTicketHasta(event.target.value)} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 2 }}><TextField select fullWidth size="small" label="Vista" value={vistaTickets} onChange={(event) => setVistaTickets(event.target.value)}><MenuItem value="usuario">Per usuari</MenuItem><MenuItem value="evento">Per esdeveniment</MenuItem><MenuItem value="concepto">Per concepte comptable</MenuItem></TextField></Grid>
                        </Grid>
                        {errorTicket && <Typography color="error" sx={{ mb: 1 }}>{errorTicket}</Typography>}
                        <TableContainer className="accounting-table-wrap"><Table size="small" className="accounting-tickets-table"><TableHead><TableRow><TableCell>Membre</TableCell><TableCell>Esdeveniment</TableCell><TableCell>Data</TableCell><TableCell>Concepte comptable</TableCell><TableCell>Tiquet</TableCell><TableCell align="right">Import</TableCell><TableCell>Estat</TableCell><TableCell align="right">Acció</TableCell></TableRow></TableHead><TableBody>
                            {ticketsFiltrados.map((ticket, index) => {
                                const pagado = Number(ticket.pagado) === 1;
                                const compensado = Number(ticket.pago_compensado || 0) === 1;
                                const campoVista = { usuario: 'usuario', evento: 'evento', concepto: 'concepto_contable' }[vistaTickets];
                                const etiquetaGrupo = ticket[campoVista] || 'Sense assignar';
                                const grupoAnterior = index > 0 ? ticketsFiltrados[index - 1][campoVista] || 'Sense assignar' : null;
                                const grupoSiguiente = index < ticketsFiltrados.length - 1 ? ticketsFiltrados[index + 1][campoVista] || 'Sense assignar' : null;
                                const esFinalGrupo = etiquetaGrupo !== grupoSiguiente;
                                const subtotalGrupo = esFinalGrupo ? totalizarTickets(ticketsFiltrados.filter((item) => (item[campoVista] || 'Sense assignar') === etiquetaGrupo)) : null;
                                return <React.Fragment key={`${ticket.tipo_cobro}-${ticket.id}`}>{etiquetaGrupo !== grupoAnterior && <TableRow className="accounting-ticket-group-row"><TableCell colSpan={8} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>{etiquetaGrupo}</TableCell></TableRow>}<TableRow className={compensado ? 'accounting-compensated-row' : (pagado ? '' : 'accounting-unpaid-row')}><TableCell>{ticket.usuario}</TableCell><TableCell>{ticket.evento}</TableCell><TableCell>{ticket.fecha}</TableCell><TableCell>{ticket.concepto_contable || 'Sense assignar'}</TableCell><TableCell>{ticket.ticket} × {ticket.cantidad}</TableCell><TableCell align="right">{formatoEuro(ticket.total)}</TableCell><TableCell><Typography variant="caption" className={compensado ? 'accounting-compensated-text' : 'accounting-caption'}>{compensado ? 'Compensat' : (pagado ? 'Pagat' : 'Pendent de pagar')}</Typography></TableCell><TableCell align="right">{puedeRegistrarIngresos && <Tooltip title={pagado ? 'Tornar a pendent de pagar' : 'Marcar com a pagat'}><IconButton size="small" color={pagado ? 'default' : 'success'} onClick={() => abrirPagoTicket(ticket)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>}</TableCell></TableRow>{esFinalGrupo && <TableRow className="accounting-total-row"><TableCell colSpan={5}><strong>Subtotal {etiquetaGrupo}</strong></TableCell><TableCell colSpan={3} align="right">Pagat: {formatoEuro(subtotalGrupo.pagado)} · Compensat: {formatoEuro(subtotalGrupo.compensado)} · Pendent: {formatoEuro(subtotalGrupo.pendiente)} · Total: {formatoEuro(subtotalGrupo.total)}</TableCell></TableRow>}</React.Fragment>;
                            })}
                            {ticketsFiltrados.length === 0 && <TableRow><TableCell colSpan={8}>No hi ha tiquets amb aquests filtres.</TableCell></TableRow>}
                            {ticketsFiltrados.length > 0 && <TableRow className="accounting-total-row"><TableCell colSpan={5}><strong>Total</strong></TableCell><TableCell colSpan={3} align="right">Pagat: {formatoEuro(totalesTickets.pagado)} · Compensat: {formatoEuro(totalesTickets.compensado)} · Pendent: {formatoEuro(totalesTickets.pendiente)} · Total: {formatoEuro(totalesTickets.total)}</TableCell></TableRow>}
                        </TableBody></Table></TableContainer>
                    </AccordionDetails>
                </Accordion>
            )}

            {(
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Accordion className="accounting-user-ledger" elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><CabeceraListaContable titulo="Ingressos pendents" cantidad={ingresosPendientes.length} confirmado={totalIngresosConfirmadosTabla} pendiente={totalIngresosPendientesTabla} total={totalIngresosPendientesTabla + totalIngresosConfirmadosTabla} /></AccordionSummary>
                        <AccordionDetails><TableContainer className="accounting-table-wrap"><Table size="small" className="accounting-pending-table accounting-income-pending-table"><TableHead><TableRow><TableCell>Concepte</TableCell><TableCell>Usuari</TableCell><TableCell>Data</TableCell><TableCell align="right">Import</TableCell><TableCell align="right">Acció</TableCell></TableRow></TableHead><TableBody>{ingresosPendientes.length ? ingresosPendientes.map((ingreso) => <TableRow key={ingreso.id_ingreso} className="accounting-unpaid-row"><TableCell>{ingreso.tipo_ingreso || '-'}</TableCell><TableCell>{usuarios.find((usuario) => String(usuario.id_usuario) === String(ingreso.id_usuario))?.nombre_usuario || 'General'}</TableCell><TableCell>{formatoFecha(ingreso.fecha_creacion)}</TableCell><TableCell align="right">{formatoEuro(ingreso.cantidad)}</TableCell><TableCell align="right">{puedeRegistrarIngresos && <Tooltip title="Confirmar ingrés"><IconButton size="small" color="success" onClick={(event) => abrirMarcarIngresoPagado(event, ingreso)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>}</TableCell></TableRow>) : <TableRow><TableCell colSpan={5}>No hi ha ingressos pendents.</TableCell></TableRow>}</TableBody></Table></TableContainer></AccordionDetails>
                    </Accordion>
                    <Accordion className="accounting-user-ledger" elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><CabeceraListaContable titulo="Despeses pendents" cantidad={gastosPendientes.length} confirmado={totalGastosConfirmadosTabla} pendiente={totalGastosPendientesTabla} total={totalGastosPendientesTabla + totalGastosConfirmadosTabla} esGasto /></AccordionSummary>
                        <AccordionDetails><TableContainer className="accounting-table-wrap"><Table size="small" className="accounting-pending-table accounting-expense-pending-table"><TableHead><TableRow><TableCell>Concepte</TableCell><TableCell>Usuari</TableCell><TableCell>Descripció</TableCell><TableCell align="right">Import</TableCell><TableCell align="right">Acció</TableCell></TableRow></TableHead><TableBody>{gastosPendientes.length ? gastosPendientes.map((gasto) => <TableRow key={gasto.id_gasto} className="accounting-unpaid-row"><TableCell>{gasto.tipo_gasto}</TableCell><TableCell>{gasto.nombre_usuario || 'Usuari'}</TableCell><TableCell>{gasto.descripcion || '-'}</TableCell><TableCell align="right">{formatoGasto(gasto.cantidad)}</TableCell><TableCell align="right">{puedeRegistrarIngresos && <Tooltip title="Marcar com a pagada"><IconButton size="small" color="success" onClick={(event) => abrirMarcarPagado(event, gasto, gasto.tipo_gasto)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>}</TableCell></TableRow>) : <TableRow><TableCell colSpan={5}>No hi ha despeses pendents.</TableCell></TableRow>}</TableBody></Table></TableContainer></AccordionDetails>
                    </Accordion>
                    {puedeRegistrarIngresos && (
                        <Accordion className="accounting-user-ledger" elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className="accounting-list-header" sx={{ minWidth: '100%', minHeight: '9vh' }}>
                                    <Typography variant="h6" className="accounting-list-title">Pagaments compensats ({pagosCompensados.length})</Typography>
                                    <Box className="accounting-amount-summary-total" style={{ borderTop: '0px' }}>
                                        <span>Compensat:</span>
                                        <strong className="accounting-compensated-text">{formatoEuro(totalPagosCompensados)}
                                        </strong>
                                    </Box>
                                </Box>

                            </AccordionSummary>
                            <AccordionDetails>
                                {pagosCompensadosPorPersona.length ? (
                                    <Stack spacing={1}>
                                        {pagosCompensadosPorPersona.map((grupo) => (
                                            <Accordion key={grupo.key} className="accounting-person-accordion accounting-compensated-person" elevation={0}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Box className="accounting-list-header">
                                                        <Box>
                                                            <Typography className="accounting-person-name">{grupo.usuario}</Typography>
                                                            <Typography className="accounting-person-count">{grupo.tickets.length} pagaments</Typography>
                                                        </Box>
                                                        <Typography className="accounting-compensated-text">{formatoEuro(grupo.total)}</Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <TableContainer className="accounting-table-wrap">
                                                        <Table size="small" className="accounting-pending-table accounting-compensated-table">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Data</TableCell>
                                                                    <TableCell>Esdeveniment</TableCell>
                                                                    <TableCell>Tiquet</TableCell>
                                                                    <TableCell>Concepte</TableCell>
                                                                    <TableCell align="right">Compensat</TableCell>
                                                                    <TableCell align="right">Accions</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody >
                                                                {grupo.tickets.map((ticket) => (
                                                                    <TableRow style={{ backgroundColor: 'white !important' }} key={`${ticket.tipo_cobro}-${ticket.id}`}>
                                                                        <TableCell>{formatoFecha(ticket.fecha)}</TableCell>
                                                                        <TableCell>{ticket.evento}</TableCell>
                                                                        <TableCell>{ticket.ticket} × {ticket.cantidad}</TableCell>
                                                                        <TableCell>{ticket.concepto_contable || 'Sense assignar'}</TableCell>
                                                                        <TableCell align="right" className="accounting-money">{formatoEuro(ticket.total)}</TableCell>
                                                                        <TableCell align="right">
                                                                            <Tooltip title="Descompensar i tornar a pendent">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="secondary"
                                                                                    onClick={(event) => abrirDescompensarTicket(event, ticket)}
                                                                                >
                                                                                    <UndoIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography className="accounting-empty-text">No hi ha pagaments compensats.</Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Stack>
            )}

            <Stack className="accounting-sections">
                {datosContables.map((apartadoData, index) => {
                    const ingresosApartado = calcularTotalIngresos(apartadoData);
                    const gastosApartado = calcularTotalGastos(apartadoData);
                    const ingresosManualesProvisionalesApartado = (apartadoData.tipos_ingresos ?? []).reduce((total, tipo) => total + (tipo.ingresos ?? []).reduce((subtotal, ingreso) => Number(ingreso.pagado) === 1 ? subtotal : subtotal + Number(ingreso.cantidad || 0), 0), 0);
                    const ticketsPendientesApartado = totalizarTickets(ticketsContables.filter((ticket) => String(ticket.concepto_contable || '') === String(apartadoData.apartado) && Number(ticket.pagado) !== 1)).pendiente;
                    const ingresosProvisionalesApartado = ingresosManualesProvisionalesApartado + ticketsPendientesApartado;
                    const ingresosConfirmadosApartado = ingresosApartado - ingresosManualesProvisionalesApartado;
                    const gastosProvisionalesApartado = (apartadoData.tipos_gastos ?? []).reduce((total, tipo) => total + (tipo.gastos ?? []).reduce((subtotal, gasto) => Number(gasto.pagado) === 1 ? subtotal : subtotal + Number(gasto.cantidad || 0), 0), 0);
                    const gastosConfirmadosApartado = gastosApartado - gastosProvisionalesApartado;
                    const ingresosFinalApartado = ingresosProvisionalesApartado + ingresosConfirmadosApartado;
                    const gastosFinalApartado = gastosProvisionalesApartado + gastosConfirmadosApartado;
                    const balanceProvisionalApartado = ingresosProvisionalesApartado - gastosProvisionalesApartado;
                    const balanceConfirmadoApartado = ingresosConfirmadosApartado - gastosConfirmadosApartado;
                    const balanceFinalApartado = balanceProvisionalApartado + balanceConfirmadoApartado;
                    const resumenConceptosApartado = getResumenConceptosApartado(apartadoData);
                    const registrosIngresoConfirmados = (apartadoData.tipos_ingresos ?? []).flatMap((tipo) =>
                        (tipo.ingresos ?? []).map((ingreso) => {
                            const usuario = usuarios.find(user => String(user.id_usuario) === String(ingreso.id_usuario));
                            const esGeneral = Number(ingreso.ingreso_general) === 1 || ingreso.id_usuario === null;

                            return {
                                ...ingreso,
                                tipo_ingreso: tipo.tipo_ingreso,
                                id_tipo_ingreso: tipo.id_tipo_ingreso,
                                id_tipo_dato: tipo.id_tipo_dato,
                                nombre_usuario: esGeneral ? 'General' : usuario?.nombre_usuario || 'Usuari',
                            };
                        })
                    );
                    const registrosTiquetsPendents = ticketsContables
                        .filter((ticket) => String(ticket.concepto_contable || '') === String(apartadoData.apartado) && Number(ticket.pagado) !== 1)
                        .map((ticket) => ({
                            id_ingreso: null,
                            id: ticket.id,
                            id_evento: ticket.id_evento || null,
                            tipo_cobro: ticket.tipo_cobro || 'ticket',
                            id_usuario: null,
                            ingreso_general: 1,
                            nombre_usuario: ticket.usuario,
                            evento: ticket.evento,
                            ticket: ticket.ticket,
                            tipo_ingreso: `${ticket.evento} — ${ticket.ticket} × ${ticket.cantidad}`,
                            id_concepto_contabilidad: ticket.id_concepto_contabilidad || null,
                            nombre_concepto_contabilidad: ticket.nombre_concepto_contabilidad || 'No definit',
                            id_tipo_dato: 1,
                            cantidad: ticket.total,
                            total: ticket.total,
                            fecha_creacion: ticket.fecha,
                            pagado: 0,
                            ticket_pendiente: true,
                            es_ingreso_ticket: true,
                            puede_marcar_pagado: puedeRegistrarIngresos ? 1 : 0,
                            puede_eliminar: 0
                        }));
                    const registrosIngreso = [...registrosIngresoConfirmados, ...registrosTiquetsPendents]
                        .sort((a, b) => String(b.fecha_creacion || '').localeCompare(String(a.fecha_creacion || '')));

                    return (
                        <Accordion key={index} className="accounting-accordion" elevation={0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className="accounting-summary">
                                    <IconButton
                                        size="small"
                                        className="accounting-history-button"
                                        onClick={(event) => abrirRegistros(event, apartadoData.id_apartado)}
                                    >
                                        <HistoryIcon fontSize="small" />
                                    </IconButton>
                                    <Box className="accounting-summary-main" sx={{ minWidth: '100%' }}>

                                        <Grid
                                            container
                                            direction="row"
                                            sx={{
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                            size={{ xs: 12 }}
                                        >
                                            <Typography variant="h6" className="accounting-section-title">
                                                {apartadoData.apartado}
                                            </Typography>
                                            <Grid item xs={'auto'}>
                                                {puedeRegistrarIngresos && <Tooltip title="Editar apartat"><IconButton size="small" sx={{ ml: 1 }} onClick={(event) => abrirEditarConcepto(event, apartadoData)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                                                {puedeRegistrarIngresos && <Tooltip title="Eliminar concepte"><IconButton size="small" color="error" sx={{ ml: 0.5 }} onClick={(event) => { event.stopPropagation(); setConceptoEliminar(apartadoData); setErrorConcepto(''); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                                            </Grid>
                                        </Grid>

                                    </Box>
                                    <ResumenGeneralApartado
                                        ingresosConfirmados={ingresosConfirmadosApartado}
                                        ingresosPendientes={ingresosProvisionalesApartado}
                                        ingresosTotal={ingresosFinalApartado}
                                        gastosConfirmados={gastosConfirmadosApartado}
                                        gastosPendientes={gastosProvisionalesApartado}
                                        gastosTotal={gastosFinalApartado}
                                        balanceConfirmado={balanceConfirmadoApartado}
                                        balanceTotal={balanceFinalApartado}
                                        className="accounting-summary-totals-desktop"
                                    />
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails className="accounting-details">
                                <Accordion className="accounting-subaccordion accounting-mobile-general-summary" elevation={0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'var(--ki-surface-low)' }}>
                                        <Box className="accounting-subsummary">
                                            <AccountBalanceWalletIcon fontSize="small" color="primary" />
                                            <Typography className="accounting-subtitle">
                                                Resum general
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <ResumenGeneralApartado
                                            ingresosConfirmados={ingresosConfirmadosApartado}
                                            ingresosPendientes={ingresosProvisionalesApartado}
                                            ingresosTotal={ingresosFinalApartado}
                                            gastosConfirmados={gastosConfirmadosApartado}
                                            gastosPendientes={gastosProvisionalesApartado}
                                            gastosTotal={gastosFinalApartado}
                                            balanceConfirmado={balanceConfirmadoApartado}
                                            balanceTotal={balanceFinalApartado}
                                            className="accounting-summary-totals-mobile"
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className="accounting-subaccordion" elevation={0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'var(--azul)' }}>
                                        <Box className="accounting-subsummary">
                                            <ReceiptLongIcon fontSize="small" color='info' />
                                            <Typography className="accounting-subtitle">
                                                Conceptes de {apartadoData.apartado}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {puedeGestionarConceptosPredefinidos && <Box className="accounting-action-row">
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => abrirCrearConceptoPredefinido(apartadoData)}
                                            >
                                                Concepte
                                            </Button>
                                        </Box>}

                                        <Stack spacing={1.5}>
                                            {resumenConceptosApartado.length > 0 ? resumenConceptosApartado.map((concepto) => {
                                                const movimientos = (concepto.cantidadIngresos || 0) + (concepto.cantidadGastos || 0);
                                                const balanceConfirmadoConcepto = Number(concepto.ingresosConfirmados || 0) - Number(concepto.gastosConfirmados || 0);
                                                const balancePendienteConcepto = Number(concepto.ingresosPendientes || 0) - Number(concepto.gastosPendientes || 0);

                                                return (
                                                    <Accordion key={concepto.id || 'no-definit'} className="accounting-concept-detail" elevation={0}>
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Box className="accounting-concept-summary">
                                                                <Box>
                                                                    <Typography className="accounting-money">
                                                                        {concepto.nombre}
                                                                    </Typography>
                                                                    <Typography className="accounting-caption">
                                                                        {movimientos} moviments
                                                                    </Typography>
                                                                </Box>
                                                                <Stack direction="row" className="accounting-concept-totals accounting-concept-status-totals">
                                                                    <Box className="accounting-concept-total-card accounting-concept-total-income">
                                                                        <Typography variant='subtitle2' className="accounting-money">Ingressos</Typography>
                                                                        <Box className="accounting-concept-total-lines">
                                                                            <Typography className="accounting-caption">
                                                                                <span>Confirmat</span>
                                                                                <strong>{formatoEuro(concepto.ingresosConfirmados)}</strong>
                                                                            </Typography>
                                                                            <Typography className="accounting-caption">
                                                                                <span>Pendent</span>
                                                                                <strong>{formatoEuro(concepto.ingresosPendientes)}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box className="accounting-amount-summary-total">
                                                                            <Typography className="accounting-caption">
                                                                                <span>Total</span>
                                                                                <strong
                                                                                    className={
                                                                                        Number(concepto.ingresosConfirmados + concepto.ingresosPendientes) > 0
                                                                                            ? 'accounting-amount-positive'
                                                                                            : Number(concepto.ingresosConfirmados + concepto.ingresosPendientes) < 0
                                                                                                ? 'accounting-amount-negative'
                                                                                                : ''
                                                                                    }
                                                                                >
                                                                                    {formatoEuro(concepto.ingresosConfirmados + concepto.ingresosPendientes)}
                                                                                </strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box className="accounting-concept-total-card accounting-concept-total-expense">
                                                                        <Typography variant='subtitle2' className="accounting-money">Despeses</Typography>
                                                                        <Box className="accounting-concept-total-lines">
                                                                            <Typography className="accounting-caption">
                                                                                <span>Confirmat</span>
                                                                                <strong>{formatoGasto(concepto.gastosConfirmados)}</strong>
                                                                            </Typography>
                                                                            <Typography className="accounting-caption">
                                                                                <span>Pendent</span>
                                                                                <strong>{formatoGasto(concepto.gastosPendientes)}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box className="accounting-amount-summary-total">
                                                                            <Typography className="accounting-caption">
                                                                                <span>Total</span>
                                                                                <strong
                                                                                    className={
                                                                                        Number(-Math.abs(Number(concepto.gastosPendientes + concepto.gastosConfirmados) || 0)) > 0
                                                                                            ? 'accounting-amount-positive'
                                                                                            : Number(-Math.abs(Number(concepto.gastosPendientes + concepto.gastosConfirmados) || 0)) < 0
                                                                                                ? 'accounting-amount-negative'
                                                                                                : ''
                                                                                    }
                                                                                >
                                                                                    {formatoEuro(-Math.abs(Number(concepto.gastosPendientes + concepto.gastosConfirmados) || 0))}
                                                                                </strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box className="accounting-concept-total-card accounting-concept-total-balance">
                                                                        <Typography variant='subtitle2' className="accounting-money">Balanç</Typography>
                                                                        <Typography className="accounting-caption">
                                                                            <span>Confirmat</span>
                                                                            <strong className={balanceConfirmadoConcepto < 0 ? 'accounting-negative-text' : 'accounting-positive-text'}>{formatoEuro(balanceConfirmadoConcepto)}</strong>
                                                                        </Typography>
                                                                        {/*<Box className="accounting-concept-total-lines">
                                                                             <Typography className="accounting-caption">
                                                                                <span>Pendent</span>
                                                                                <strong className={balancePendienteConcepto < 0 ? 'accounting-negative-text' : 'accounting-positive-text'}>{formatoEuro(balancePendienteConcepto)}</strong>
                                                                            </Typography> 
                                                                        </Box>*/}
                                                                        <Box className="accounting-amount-summary-total">
                                                                            <Typography className="accounting-caption">
                                                                                <span>Total</span>
                                                                                <strong
                                                                                    className={
                                                                                        Number(balanceConfirmadoConcepto + balancePendienteConcepto) > 0
                                                                                            ? 'accounting-amount-positive'
                                                                                            : Number(balanceConfirmadoConcepto + balancePendienteConcepto) < 0
                                                                                                ? 'accounting-amount-negative'
                                                                                                : ''
                                                                                    }
                                                                                >
                                                                                    {formatoEuro(balanceConfirmadoConcepto + balancePendienteConcepto)}
                                                                                </strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Stack>
                                                                <Box className="accounting-concept-actions">
                                                                    {puedeGestionarConceptosPredefinidos && concepto.id ? (
                                                                        <>
                                                                            <Tooltip title="Editar concepte predefinit">
                                                                                <IconButton size="small" onClick={(event) => { event.stopPropagation(); abrirEditarConceptoPredefinido(apartadoData, concepto); }}>
                                                                                    <EditIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Eliminar concepte">
                                                                                <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); setConceptoPredefinidoEliminar({ apartado: apartadoData, concepto }); }}>
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>
                                                                    ) : ''}
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Grid container spacing={2}>
                                                                <Grid size={{ xs: 12, md: 6 }}>
                                                                    <Typography className="accounting-subtitle accounting-concept-table-title">
                                                                        Ingressos
                                                                    </Typography>
                                                                    <TableContainer component={Paper} elevation={0} className="accounting-table-wrap">
                                                                        <Table size="small" className="accounting-table accounting-income-table">
                                                                            <TableBody>
                                                                                {renderMovimientosConceptoPorUsuario(concepto, concepto.registrosIngresos, 'ingreso', apartadoData.id_apartado)}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableContainer>
                                                                </Grid>
                                                                <Grid size={{ xs: 12, md: 6 }}>
                                                                    <Typography className="accounting-subtitle accounting-concept-table-title">
                                                                        Despeses
                                                                    </Typography>
                                                                    <TableContainer component={Paper} elevation={0} className="accounting-table-wrap">
                                                                        <Table size="small" className="accounting-table accounting-expense-table">
                                                                            <TableBody>
                                                                                {renderMovimientosConceptoPorUsuario(concepto, concepto.registrosGastos, 'gasto', apartadoData.id_apartado)}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableContainer>
                                                                </Grid>
                                                            </Grid>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            }) : (
                                                <Box className="accounting-empty-cell">
                                                    No hi ha conceptes predefinits.
                                                </Box>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className="accounting-subaccordion" elevation={0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'var(--verde)' }}>
                                        <Box className="accounting-subsummary">
                                            <TrendingUpIcon fontSize="small" color='success' />
                                            <Typography className="accounting-subtitle">
                                                Desglossament d'ingressos
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {puedeRegistrarIngresos && <Box className="accounting-action-row">
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setPagoUsuarioApartado(null);
                                                    setPagoGeneralApartado(null);
                                                    setCurrenApartado(apartadoData.id_apartado);
                                                    setModalTipoIngresoOpen(true);
                                                }}
                                            >
                                                Ingrés
                                            </Button>
                                        </Box>}

                                        <TableContainer component={Paper} elevation={0} className="accounting-table-wrap">
                                            <Table size="small" className="accounting-table accounting-income-table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Usuari</TableCell>
                                                        <TableCell align="right">Import</TableCell>
                                                        <TableCell>Estat</TableCell>
                                                        <TableCell align="right">Accions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {registrosIngreso.length > 0 ? registrosIngreso.map((ingreso) => {
                                                        const esTexto = ingreso.id_tipo_dato === 2;
                                                        const esGeneral = Number(ingreso.ingreso_general) === 1 || ingreso.id_usuario === null;
                                                        const usuario = {
                                                            id_usuario: ingreso.id_usuario,
                                                            nombre_usuario: ingreso.nombre_usuario
                                                        };
                                                        const tipo = {
                                                            tipo_ingreso: ingreso.tipo_ingreso,
                                                            id_tipo_ingreso: ingreso.id_tipo_ingreso
                                                        };

                                                        return (
                                                            <TableRow
                                                                key={ingreso.id_ingreso || `${ingreso.tipo_cobro || 'ingreso'}-${ingreso.id || `${ingreso.id_usuario}-${ingreso.id_tipo_ingreso}`}`}
                                                                className={`accounting-table-row ${Number(ingreso.pagado) === 1 ? '' : 'accounting-unpaid-row'} ${puedeRegistrarIngresos ? 'accounting-editable-row' : ''}`}
                                                                onClick={() => {
                                                                    if (puedeRegistrarIngresos) {
                                                                        handleCellClick(
                                                                            ingreso.nombre_usuario,
                                                                            ingreso.id_usuario,
                                                                            ingreso.tipo_ingreso,
                                                                            ingreso.id_tipo_ingreso,
                                                                            ingreso.cantidad ?? 0,
                                                                            ingreso,
                                                                            apartadoData.id_apartado
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                <TableCell className="accounting-user-cell">
                                                                    <Box className="accounting-expense-amount">
                                                                        <ReceiptLongIcon fontSize="small" />
                                                                        <Box>
                                                                            <Typography className="accounting-money">
                                                                                {ingreso.nombre_usuario}
                                                                            </Typography>
                                                                            <Typography className="accounting-caption">
                                                                                {ingreso.tipo_ingreso}
                                                                            </Typography>
                                                                            <Typography className="accounting-caption">
                                                                                {ingreso.nombre_concepto_contabilidad || 'No definit'}
                                                                            </Typography>
                                                                            {formatoFecha(ingreso.fecha_creacion) ? (
                                                                                <Typography className="accounting-caption">
                                                                                    {formatoFecha(ingreso.fecha_creacion)}
                                                                                </Typography>
                                                                            ) : ''}
                                                                            {esGeneral && ingreso.nombre_usuario_creador ? (
                                                                                <Typography className="accounting-caption">
                                                                                    Afegit per {ingreso.nombre_usuario_creador}
                                                                                </Typography>
                                                                            ) : ''}
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell className="accounting-money">
                                                                    {esTexto ? ingreso.cantidad : formatoEuro(ingreso.cantidad)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="caption" className="accounting-caption">
                                                                        {Number(ingreso.pagado) === 1 ? 'Confirmat' : ingreso.ticket_pendiente ? 'Provisional · Tiquet pendent' : 'Provisional'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right" className="accounting-money">
                                                                    {ingreso.ticket_pendiente && puedeRegistrarIngresos ? (
                                                                        <Tooltip title="Confirmar tiquet">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="success"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    abrirPagoTicket(ingreso);
                                                                                }}
                                                                            >
                                                                                <CheckCircleIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : Number(ingreso.puede_marcar_pagado) === 1 ? (
                                                                        <Tooltip title="Confirmar ingr&eacute;s">
                                                                            <IconButton size="small" color="success" onClick={(event) => abrirMarcarIngresoPagado(event, ingreso)}>
                                                                                <CheckCircleIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : Number(ingreso.puede_desmarcar_pagado) === 1 ? (
                                                                        <Tooltip title="Tornar ingr&eacute;s a pendent">
                                                                            <IconButton size="small" color="secondary" onClick={(event) => abrirDesmarcarIngresoPagado(event, ingreso, usuario, tipo)}>
                                                                                <UndoIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : ''}
                                                                    {ingreso?.id_ingreso && Number(ingreso.puede_eliminar) === 1 ? (
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            className="accounting-inline-delete"
                                                                            onClick={(event) => abrirEliminarIngreso(event, ingreso, usuario, tipo)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    ) : ''}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }) : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} className="accounting-empty-cell">
                                                                No hi ha ingressos registrats.
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className="accounting-subaccordion" elevation={0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'var(--rojo)' }}>
                                        <Box className="accounting-subsummary">
                                            <TrendingDownIcon fontSize="small" color='error' />
                                            <Typography className="accounting-subtitle">
                                                Desglossament de despeses
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {puedeRegistrarIngresos && <Box className="accounting-action-row">
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setCurrenApartado(apartadoData.id_apartado);
                                                    setModalAnadirGastoOpen(true);
                                                }}
                                            >
                                                Despesa
                                            </Button>
                                        </Box>}

                                        <TableContainer component={Paper} elevation={0} className="accounting-table-wrap">
                                            <Table size="small" className="accounting-table accounting-expense-table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Quantitat</TableCell>
                                                        <TableCell align="center">Imatge</TableCell>
                                                        <TableCell>Observació</TableCell>
                                                        <TableCell align="right">Accions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(apartadoData.tipos_gastos ?? []).flatMap(tipo =>
                                                        (tipo.gastos ?? []).map((gasto, i) => {
                                                            return (
                                                                <TableRow
                                                                    key={`${tipo.tipo_gasto}-${i}`}
                                                                    className={`accounting-table-row ${Number(gasto.pagado) === 1 ? '' : 'accounting-unpaid-row'} ${Number(gasto.puede_eliminar) === 1 ? 'accounting-editable-row' : ''}`}
                                                                    onClick={() => abrirEditarGasto(gasto, tipo.tipo_gasto)}
                                                                >
                                                                    <TableCell>
                                                                        <Box className="accounting-expense-amount">
                                                                            <ReceiptLongIcon fontSize="small" />
                                                                            <Box>
                                                                                <Typography className="accounting-money">
                                                                                    {gasto.nombre_usuario || 'Usuari'}
                                                                                </Typography>
                                                                                <Typography className="accounting-money">
                                                                                    {formatoGasto(gasto.cantidad)}
                                                                                </Typography>
                                                                                <Typography className="accounting-caption">
                                                                                    {tipo.tipo_gasto}
                                                                                </Typography>
                                                                                <Typography className="accounting-caption">
                                                                                    {gasto.nombre_concepto_contabilidad || 'No definit'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </TableCell>

                                                                    <TableCell align="center">
                                                                        {gasto.imagen ? (
                                                                            <>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    className="accounting-image-button"
                                                                                    onClick={(event) => {
                                                                                        event.stopPropagation();
                                                                                        setGastoImagen({ ...gasto, tipo_gasto: tipo.tipo_gasto });
                                                                                    }}
                                                                                >
                                                                                    <ImageIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </>
                                                                        ) : '-'}
                                                                    </TableCell>

                                                                    <TableCell className="accounting-description-cell">
                                                                        <Box>
                                                                            <Typography variant='body2' sx={{ fontSize: 11 }}>
                                                                                {gasto.descripcion ?? '-'}
                                                                            </Typography>
                                                                            <Typography variant='caption' className="accounting-caption">
                                                                                {gasto.pagado === 1 ? 'Pagat' : 'No pagat'}
                                                                            </Typography>
                                                                        </Box>

                                                                    </TableCell>


                                                                    <TableCell align="right">
                                                                        <Grid
                                                                            container
                                                                            direction="row"
                                                                            sx={{
                                                                                justifyContent: 'end'

                                                                            }}
                                                                            size={{ xs: 12 }}
                                                                        >
                                                                            {Number(gasto.puede_marcar_pagado) === 1 ? (
                                                                                <Tooltip title="Marcar com a pagada">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="success"
                                                                                        onClick={(event) => abrirMarcarPagado(event, gasto, tipo.tipo_gasto)}
                                                                                    >
                                                                                        <CheckCircleIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            ) : ''}
                                                                            {Number(gasto.puede_eliminar) === 1 ? (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="error"
                                                                                    onClick={(event) => {
                                                                                        event.stopPropagation();
                                                                                        abrirEliminarGasto(gasto, tipo.tipo_gasto);
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            ) : ''}
                                                                        </Grid>

                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    )}

                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>

            {opciones == 1 && (
                <>
                    <AnadirIngresoDialog
                        open={modalAddOpen}
                        onClose={() => setModalAddOpen(false)}
                        onAdd={handleAdd}
                        usuarios={usuarios}
                        tiposIngreso={tiposIngreso}
                        year={anioSeleccionado}
                        anios={listaAnios}
                        cargarDatos={cargarDatos}
                    />

                    <EditarIngresoDialog
                        open={puedeRegistrarIngresos && modalEditOpen}
                        onClose={() => setModalEditOpen(false)}
                        ingreso={ingresoSeleccionado}
                        id_tipo_ingreso={ingresoSeleccionado?.id_tipo_ingreso}
                        currenApartado={currenApartado}
                        cargarDatos={cargarDatos}
                        conceptos={getConceptosApartadoActual()}
                    />
                </>
            )}
            <AnadirTipoIngresoDialog
                open={puedeRegistrarIngresos && modalTipoIngresoOpen}
                onClose={() => {
                    setModalTipoIngresoOpen(false);
                    setPagoUsuarioApartado(null);
                    setPagoGeneralApartado(null);
                }}
                onAdd={handleAddTipoIngreso}
                currenApartado={currenApartado}
                cargarDatos={cargarDatos}
                year={anioSeleccionado}
                usuarios={usuarios}
                usuarioActualId={usuarioActualId}
                selectorUsuarioBloqueado={Boolean(pagoUsuarioApartado) || opciones != 1}
                usuarioPreseleccionadoId={pagoUsuarioApartado?.usuario?.id_usuario || ''}
                ingresoGeneralPreseleccionado={Boolean(pagoGeneralApartado)}
                conceptos={getConceptosApartadoActual()}
            />
            <AnadirGastoDialog
                open={modalAnadirGastoOpen}
                onClose={() => setModalAnadirGastoOpen(false)}
                onAdd={handleAdd}
                year={anioSeleccionado}
                cargarDatos={cargarDatos}
                currentApartado={currenApartado}
                usuarios={usuarios}
                usuarioActualId={usuarioActualId}
                selectorUsuarioBloqueado={opciones != 1}
                conceptos={getConceptosApartadoActual()}
                apartados={datosContables}
            />
            <EditarGastoDialog
                open={modalEditarGastoOpen}
                onClose={() => {
                    setModalEditarGastoOpen(false);
                    setGastoSeleccionado(null);
                }}
                gasto={gastoSeleccionado}
                cargarDatos={cargarDatos}
                conceptos={getConceptosApartadoActual()}
            />
            <Dialog open={Boolean(ticketPagoParcial)} onClose={() => { setTicketPagoParcial(null); setPagoTicketCompensado(false); }} maxWidth="xs" fullWidth>
                <DialogTitle>Confirmar cobrament de tiquet</DialogTitle>
                <DialogContent>
                    <Typography className="accounting-delete-copy" sx={{ mb: 2 }}>
                        {ticketPagoParcial?.ticket} · Total: {formatoEuro(ticketPagoParcial?.total)}
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={pagoTicketCompensado} onChange={(event) => setPagoTicketCompensado(event.target.checked)} />}
                        label="Pago compensado"
                        className="accounting-compensated-check"
                    />
                    <TextField autoFocus fullWidth type="number" label="Import pagat" value={importePagoParcial} onChange={(event) => setImportePagoParcial(event.target.value)} disabled={pagoTicketCompensado || ticketPagoParcial?.tipo_cobro === 'miembro'} inputProps={{ min: '0.01', max: ticketPagoParcial?.total || 0, step: '0.01' }} />
                    <Typography className="accounting-caption" sx={{ mt: 1 }}>
                        {ticketPagoParcial?.tipo_cobro === 'miembro'
                            ? 'El cobrament de membre es confirmarà sempre pel total.'
                            : pagoTicketCompensado
                                ? 'El tiquet quedarà saldat, però no generarà cap ingrés comptable.'
                                : 'Si l\'import és inferior, es crearà un nou tiquet amb la resta pendent.'}
                    </Typography>
                    {errorTicket && <Typography color="error" className="accounting-dialog-error">{errorTicket}</Typography>}
                </DialogContent>
                <DialogActions><Button onClick={() => { setTicketPagoParcial(null); setPagoTicketCompensado(false); }}>Cancel·lar</Button><Button variant="contained" color={pagoTicketCompensado ? 'secondary' : 'success'} onClick={confirmarPagoTicket}>Confirmar</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(ticketDescompensar)} onClose={cerrarDescompensarTicket} maxWidth="xs" fullWidth>
                <DialogTitle>Descompensar pagament</DialogTitle>
                <DialogContent>
                    <Typography className="accounting-delete-copy" sx={{ mb: 2 }}>
                        Aquest pagament tornarà a quedar pendent i deixarà de constar com a compensat.
                    </Typography>
                    <Stack spacing={0.5}>
                        <Typography><strong>{ticketDescompensar?.usuario}</strong></Typography>
                        <Typography className="accounting-caption">{ticketDescompensar?.evento}</Typography>
                        <Typography className="accounting-caption">
                            {ticketDescompensar?.ticket} × {ticketDescompensar?.cantidad} · {formatoFecha(ticketDescompensar?.fecha)}
                        </Typography>
                        <Typography className="accounting-compensated-text">
                            Compensat: {formatoEuro(ticketDescompensar?.total)}
                        </Typography>

                    </Stack>
                    {errorTicket && <Typography color="error" className="accounting-dialog-error">{errorTicket}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarDescompensarTicket} disabled={descompensandoTicket}>Cancel·lar</Button>
                    <Button variant="contained" color="secondary" onClick={confirmarDescompensarTicket} disabled={descompensandoTicket}>
                        Descompensar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(gastoImagen)} onClose={() => setGastoImagen(null)} fullWidth maxWidth="md">
                <DialogTitle className="accounting-image-dialog-title">
                    <Box>
                        <Typography className="accounting-eyebrow">Justificant</Typography>
                        <Typography variant="h6">
                            {gastoImagen?.tipo_gasto || 'Despesa'}
                        </Typography>
                    </Box>
                    <Typography className="accounting-image-dialog-price">
                        {formatoEuro(gastoImagen?.cantidad || 0)}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers className="accounting-image-dialog-content">
                    <Box className="accounting-image-dialog-meta">
                        <Box>
                            <Typography className="accounting-caption">Afegit per</Typography>
                            <Typography className="accounting-money">
                                {gastoImagen?.nombre_usuario || 'Usuari'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className="accounting-caption">Descripció</Typography>
                            <Typography className="accounting-image-dialog-description">
                                {gastoImagen?.descripcion || '-'}
                            </Typography>
                        </Box>
                    </Box>
                    {gastoImagen?.imagen && (
                        <Box className="accounting-image-dialog-frame">
                            <ImagenPrivada
                                path={gastoImagen.imagen}
                                alt={gastoImagen.tipo_gasto || 'Imatge de la despesa'}
                                className="ki-full-image"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setGastoImagen(null)}>
                        Tancar
                    </Button>
                </DialogActions>
            </Dialog>
            {dialogRegistros && (
                <RegistroAccionesContabilidadDialog
                    open={dialogRegistros}
                    setOpen={setDialogRegistros}
                    idEvento={idApartado}
                />
            )}
            <Dialog open={Boolean(gastoPagado)} onClose={() => setGastoPagado(null)} fullWidth maxWidth="xs">
                <DialogTitle>Marcar com a pagada</DialogTitle>
                <DialogContent dividers>
                    {errorMarcarPagado && (
                        <Typography color="error" className="accounting-dialog-error">
                            {errorMarcarPagado}
                        </Typography>
                    )}
                    <Typography>
                        Marcaràs com a pagada la despesa "{gastoPagado?.tipo_gasto}" de {formatoEuro(gastoPagado?.cantidad || 0)}.
                    </Typography>
                    <Typography className="accounting-caption">
                        Persona que l'ha afegida: {gastoPagado?.nombre_usuario || 'Usuari'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGastoPagado(null)} disabled={marcandoPagado}>
                        Cancel·lar
                    </Button>
                    <Button variant="contained" color="success" onClick={confirmarMarcarPagado} disabled={marcandoPagado}>
                        {marcandoPagado ? 'Marcant...' : 'Marcar pagada'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(gastoEliminar)} onClose={() => setGastoEliminar(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Eliminar despesa</DialogTitle>
                <DialogContent>
                    <Typography className="accounting-delete-copy">
                        S'eliminarà la despesa {gastoEliminar?.tipo_gasto || ''} per {formatoEuro(gastoEliminar?.cantidad || 0)}.
                    </Typography>
                    {errorEliminarGasto && (
                        <Typography color="error" className="accounting-delete-error">
                            {errorEliminarGasto}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGastoEliminar(null)} disabled={eliminandoGasto}>
                        Cancel·lar
                    </Button>
                    <Button color="error" variant="contained" onClick={confirmarEliminarGasto} disabled={eliminandoGasto}>
                        {eliminandoGasto ? 'Eliminant...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(ingresoEliminar)} onClose={() => setIngresoEliminar(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{ingresoEliminar?.es_ingreso_ticket ? 'Ingrés de tiquet' : 'Eliminar ingrés'}</DialogTitle>
                <DialogContent>
                    <Typography className="accounting-delete-copy">
                        {ingresoEliminar?.es_ingreso_ticket
                            ? 'Aquest ingrés prové d’un tiquet. Per llevar-lo, has de desmarcar el tiquet com a pagat en Gestió de tiquets.'
                            : `S'eliminarà l'ingrés de ${ingresoEliminar?.nombre_usuario || 'aquest usuari'} en ${ingresoEliminar?.tipo_ingreso || 'aquest concepte'} per ${formatoEuro(ingresoEliminar?.cantidad || 0)}.`}
                    </Typography>
                    {errorEliminarIngreso && (
                        <Typography color="error" className="accounting-delete-error">
                            {errorEliminarIngreso}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIngresoEliminar(null)} disabled={eliminandoIngreso}>
                        {ingresoEliminar?.es_ingreso_ticket ? 'Tancar' : 'Cancel·lar'}
                    </Button>
                    {!ingresoEliminar?.es_ingreso_ticket && <Button color="error" variant="contained" onClick={confirmarEliminarIngreso} disabled={eliminandoIngreso}>
                        {eliminandoIngreso ? 'Eliminant...' : 'Eliminar'}
                    </Button>}
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(ingresoPagado)} onClose={() => setIngresoPagado(null)} fullWidth maxWidth="xs">
                <DialogTitle>Confirmar ingr&eacute;s</DialogTitle>
                <DialogContent dividers>
                    {errorIngresoPagado && <Typography color="error" className="accounting-dialog-error">{errorIngresoPagado}</Typography>}
                    <Typography sx={{ mb: 2 }}>Confirmar&agrave;s l'ingr&eacute;s &quot;{ingresoPagado?.tipo_ingreso || ''}&quot;. Total pendent: {formatoEuro(ingresoPagado?.cantidad || 0)}.</Typography>
                    <TextField autoFocus fullWidth type="number" label="Import pagat" value={importeIngresoPagado} onChange={(event) => setImporteIngresoPagado(event.target.value)} inputProps={{ min: '0.01', max: ingresoPagado?.cantidad || 0, step: '0.01' }} />
                    <Typography className="accounting-caption" sx={{ mt: 1 }}>Si és inferior al total, la diferència quedarà com un nou ingrés pendent.</Typography>
                    {errorIngresoPagado && <Typography color="error" className="accounting-dialog-error">{errorIngresoPagado}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIngresoPagado(null)} disabled={marcandoIngresoPagado}>Cancel·lar</Button>
                    <Button variant="contained" color="success" onClick={confirmarMarcarIngresoPagado} disabled={marcandoIngresoPagado}>{marcandoIngresoPagado ? 'Confirmant...' : 'Confirmar'}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(ingresoDesmarcarPagado)} onClose={cerrarDesmarcarIngresoPagado} fullWidth maxWidth="xs">
                <DialogTitle>Tornar ingr&eacute;s a pendent</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 2 }}>
                        {ingresoDesmarcarPagado?.es_ingreso_ticket
                            ? 'El tiquet tornar&agrave; a quedar pendent i deixar&agrave; de comptar com a ingr&eacute;s confirmat.'
                            : 'L\'ingr&eacute;s tornar&agrave; a quedar pendent, per&ograve; no s\'eliminar&agrave; cap registre.'}
                    </Typography>
                    <Stack spacing={0.5}>
                        <Typography><strong>{ingresoDesmarcarPagado?.nombre_usuario || 'General'}</strong></Typography>
                        <Typography className="accounting-caption">{ingresoDesmarcarPagado?.tipo_ingreso || ''}</Typography>
                        <Typography className="accounting-caption">
                            {ingresoDesmarcarPagado?.nombre_concepto_contabilidad || 'No definit'}
                        </Typography>
                        <Typography className="accounting-money">
                            {formatoEuro(ingresoDesmarcarPagado?.cantidad || 0)}
                        </Typography>
                    </Stack>
                    {errorDesmarcarIngresoPagado && <Typography color="error" className="accounting-dialog-error">{errorDesmarcarIngresoPagado}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarDesmarcarIngresoPagado} disabled={desmarcandoIngresoPagado}>Cancel&middot;lar</Button>
                    <Button variant="contained" color="secondary" onClick={confirmarDesmarcarIngresoPagado} disabled={desmarcandoIngresoPagado}>
                        {desmarcandoIngresoPagado ? 'Actualitzant...' : 'Tornar a pendent'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={dialogAnyo} onClose={() => setDialogAnyo(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Crear any comptable</DialogTitle>
                <DialogContent><TextField fullWidth type="number" label="Any" value={nuevoAnyo} onChange={(event) => setNuevoAnyo(event.target.value)} /></DialogContent>
                <DialogActions><Button onClick={() => setDialogAnyo(false)}>Cancel·lar</Button><Button variant="contained" onClick={crearAnyo}>Crear</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(conceptoEditar)} onClose={() => setConceptoEditar(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar apartat</DialogTitle>
                <DialogContent >
                    <TextField sx={{ my: 2 }} autoFocus fullWidth label="Nom" value={nombreConcepto} onChange={(event) => setNombreConcepto(event.target.value)} />
                    <FormControlLabel control={<Switch checked={conceptoActivo} onChange={(event) => setConceptoActivo(event.target.checked)} />} label="Concepte actiu" />
                    {errorConcepto && <Typography color="error">{errorConcepto}
                    </Typography>}
                </DialogContent>
                <DialogActions><Button onClick={() => setConceptoEditar(null)}>Cancel·lar</Button><Button variant="contained" onClick={guardarConcepto}>Guardar</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(conceptoEliminar)} onClose={() => setConceptoEliminar(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Eliminar concepte</DialogTitle>
                <DialogContent><Typography>Eliminar &quot;{conceptoEliminar?.apartado}&quot;? Només es podrà eliminar si no té moviments, despeses ni esdeveniments associats.</Typography>{errorConcepto && <Typography color="error">{errorConcepto}</Typography>}</DialogContent>
                <DialogActions><Button onClick={() => setConceptoEliminar(null)}>Cancel·lar</Button><Button color="error" variant="contained" onClick={confirmarEliminarConcepto}>Eliminar</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(conceptoPredefinidoDialog)} onClose={cerrarConceptoPredefinidoDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {conceptoPredefinidoDialog?.concepto ? 'Editar concepte predefinit' : 'Afegir concepte predefinit'}
                </DialogTitle>
                <DialogContent>
                    <Typography className="accounting-caption" sx={{ mb: 2 }}>
                        {conceptoPredefinidoDialog?.apartado?.apartado || ''}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Nom del concepte"
                        value={nombreConceptoPredefinido}
                        onChange={(event) => setNombreConceptoPredefinido(event.target.value)}
                        inputProps={{ maxLength: 120 }}
                    />
                    {conceptoPredefinidoDialog?.concepto && (
                        <TextField
                            select
                            fullWidth
                            label="Apartat de comptabilitat"
                            value={apartadoConceptoPredefinidoId}
                            onChange={(event) => setApartadoConceptoPredefinidoId(event.target.value)}
                            sx={{ mt: 2 }}
                        >
                            {datosContables.map((apartado) => (
                                <MenuItem key={apartado.id_apartado} value={String(apartado.id_apartado)}>
                                    {apartado.apartado}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {errorConceptoPredefinido && (
                        <Typography color="error" className="accounting-dialog-error">
                            {errorConceptoPredefinido}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarConceptoPredefinidoDialog} disabled={guardandoConceptoPredefinido}>
                        Cancel·lar
                    </Button>
                    <Button variant="contained" onClick={guardarConceptoPredefinido} disabled={guardandoConceptoPredefinido}>
                        {guardandoConceptoPredefinido ? 'Guardant...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(conceptoPredefinidoEliminar)} onClose={() => setConceptoPredefinidoEliminar(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Eliminar concepte predefinit</DialogTitle>
                <DialogContent>
                    <Typography className="accounting-delete-copy">
                        El concepte "{conceptoPredefinidoEliminar?.concepto?.nombre}" passara a ser "No definit" en els moviments que el tinguen assignat.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConceptoPredefinidoEliminar(null)}>
                        Cancel·lar
                    </Button>
                    <Button color="error" variant="contained" onClick={confirmarEliminarConceptoPredefinido}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
